import type { ContentProposal, FieldDecision } from "@shared/content-proposal";
import type { MrssFeedItem } from "@shared/mrss-feed-asset";
import type { MeteredUsageReport } from "@shared/meteredUsage";
import type { MeteredMonthlyReport } from "@shared/meteredMonthly";
import type {
  AlertChannel,
  AlertRule,
  AlertRuleScope,
  DeviceGroup,
  DevicePlatform,
  EffectiveAlertRule,
  EnergyRange,
  EnergyReport,
  FleetAnalytics,
  FleetHistory,
  FirmwareImageSummary,
  FleetResponse,
  FoyerCommand,
  FoyerSettings,
  GlobalSchemeDefault,
  HealthAlertRecord,
  HealthAlertType,
  HeartbeatSnapshot,
  InstagramConnectionStatus,
  MenuFeedResult,
  VilmerActivitiesResult,
  OtaKind,
  PairingCode,
  PmsConfig,
  PremiseConfig,
  PremiseHealth,
  PowerSchedule,
  PowerScheduleRule,
  PremiseScreen,
  ProvisioningResult,
  ProvisioningSeatSummary,
  Rollout,
  RolloutAdoption,
  RolloutStatus,
  Room,
  ScreenRole,
  ScreenHistoryEntry,
  ScreenStatusEvent,
  StaffPushDevice,
  DoorSignStatus,
  DoorSignIdentityMode,
  DoorSignOrientation,
  CallContact,
  CallContactEndpoints,
  CallContactInput,
  CallContactScope,
  CallLogPage,
  ResolvedCallPolicy,
  CallPolicyConfig,
  CareProfile,
  CareProfileInput,
  CareTask,
  CareTaskStatus,
  GuestWebConfig,
  VideoCallSeat,
  VideoCallSeatSummary,
} from "@shared/types/foyer";
import type {
  ChainEngagementSummary,
  EngagementSummary,
} from "@shared/types/engagement";
import type {
  SignageTemplate,
  SignageTemplateInstance,
} from "@shared/types/signage-template";
import type { CallLinkIssueResponse } from "@shared/types/callRelative";
import type {
  IngestIncident,
  IngestPhase,
  IngestPhaseEntry,
} from "@shared/ingest-phases";
import type { IngestHoldReason } from "@shared/ingest-gate";
import type { VodWizardStep, VodWizardStepResult } from "@shared/vod-wizard";
import type { BatchIntent, BatchRollup } from "@shared/bulk-ingest";
import type {
  PackageFinding,
  PackageLayout,
  PackageVerdict,
} from "@shared/ingest-package";
import type { MediaOrientation } from "@shared/media";
import type { SupplierShellTheme } from "@shared/schemes/supplierTheme";
import type {
  ContentOwnerBrand,
  ContentOwnerIdentity,
} from "@shared/content-owner";
import type { ContentRentalSummary } from "@shared/content-rental";
import type {
  AssetPerspective,
  ChannelPerspective,
  ContentAnalyticsAxes,
  ContentAnalyticsFilters,
  ContentAnalyticsOverview,
  PackagePerspective,
  SupplierPerspective,
} from "@shared/types/contentAnalytics";
import type { ContentSubmission } from "@shared/content-submission";
import type {
  StorefrontAccount,
  StorefrontMember,
} from "@shared/storefront-customer";
import type { AgreementStatus } from "@shared/content-agreement";
import type { ContentDeal, DealState } from "@shared/content-deal";
import type { ContentStatement } from "@shared/content-statement";
import type {
  OfferOverridePatch,
  OfferReach,
  RoomOffer,
  RoomOfferOverride,
} from "@shared/types/roomOffer";
import type { OfferPaymentStatus } from "@shared/types/payment";
import type {
  ChainOfferFunnelSummary,
  OfferFunnelSummary,
} from "@shared/types/offerFunnel";
import type {
  GuestRequest,
  GuestRequestStatus,
  GuestRequestTransition,
} from "@shared/types/guestRequest";
import type {
  SignageAssignment,
  SignageCampaign,
  SignageDayPlan,
  SignageAssignmentPatch,
  SignagePlaylist,
  SignagePlaylistPreview,
  SignageGroupFollowing,
  SignageScreenGroup,
  SignageTimeline,
} from "@shared/types/signage";
// Distribution (BE-15) speaks the Content API's own shapes, faithfully ported.
import type {
  Asset,
  Genre,
  NewAsset,
  PaginationMetadata,
  Provider,
  UpdateAsset,
} from "@shared/types/content-api";
import type { Guide, GuideEventDetail } from "@shared/epg-guide";
import type { Carousel, CarouselIssue, SubPage } from "@shared/carousel";
import type { PackageRule } from "@shared/content-package";
import type {
  AppliedSort,
  AssetMatch,
  AssetSort,
  SortOrder,
} from "@shared/asset-search";
import type { Availability, RightsIssue } from "@shared/content-rights";
import type { ImageIssue, ImageType } from "@shared/vod-images";
import type { LocalisedIssue } from "@shared/vod-localised";
import type {
  DurationIssue,
  SeriesPlaybackBlocker,
  VodAssetType,
  YearIssue,
} from "@shared/vod-title";
import type { AssetPlaybackBlocker } from "@shared/channel-playability";
import type {
  CatalogueHealthPage,
  CatalogueProblemCode,
} from "@shared/catalogue-health";

/** Admin-facing summary of a room-access token (dates serialized as ISO). */
/** One line a TV shipped via its log stream (server: deviceLogStore). */
export interface DeviceLogLine {
  ts: string;
  level: string;
  message: string;
}

export interface DeviceLogPage {
  lines: DeviceLogLine[];
  /** Cursor for the next-older page; absent when there is none. */
  nextBefore?: string;
}

/** One grouped front-end error (server: clientErrorStore.groups). */
export interface ClientErrorGroup {
  fingerprint: string;
  app: string;
  kind: string;
  message: string;
  stack?: string;
  lastPath: string;
  firstRelease: string;
  lastRelease: string;
  lastUserAgent?: string;
  /** A lower bound: clients dedupe repeats and cap reports per page. */
  count: number;
  days: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

/** Why a TV's app or renderer died (server: deviceExitStore.list). */
export interface DeviceExitEvent {
  at: string;
  source: "process" | "renderer" | string;
  reason: string;
  description?: string;
  trace?: string;
  /** False for deliberate stops (update, force-stop, our own restart). */
  fault: boolean;
}

export interface GuestTokenSummary {
  id: string;
  premiseId: string;
  roomId: string | null;
  status: "active" | "revoked";
  batch: string;
  createdAt: string;
  revokedAt: string | null;
  graceUntil: string | null;
}

/** The ready-to-paste URLs for a hotel's Wi-Fi success page. Server-built so the
 *  CMS never has to know the guest surface's hostname. */
export interface GuestEmbedUrls {
  embedKey: string;
  /** For `<img src>`. */
  imageUrl: string;
  /** 302 to the guest surface, for `<a href>`. */
  linkUrl: string;
}

export interface GuestTokensResponse {
  baseUrl: string;
  rooms: { id: string; label: string }[];
  activeByRoom: Record<string, GuestTokenSummary>;
  property: GuestTokenSummary | null;
  tokens: GuestTokenSummary[];
}

export interface IssuedGuestToken {
  id: string;
  roomId: string | null;
  token: string;
  url: string;
  status: string;
  batch: string;
}

export interface GuestAnalyticsSummary {
  premiseId: string;
  days: number;
  totals: { resolves: number; views: number };
  sections: Record<string, number>;
  clicks: Record<string, number>;
  languages: Record<string, number>;
  byRoom: Record<string, number>;
  series: { day: string; resolves: number; views: number }[];
}

/** A care occupancy row as served by the care API (PHI-free - identity is only
 *  the policy-reduced `doorSignDisplayName`, never the raw name). */
export interface CareOccupancy {
  id: string;
  premiseId: string;
  roomId: string;
  personRef: string;
  externalEncounterId: string | null;
  status: "active" | "discharged";
  doorSignStatus: DoorSignStatus;
  doorSignDisplayName: string;
  admittedAt: string;
  dischargedAt: string | null;
  /** Resident-level video-call opt-out (VC-30 §4.8). True = this resident
   *  cannot be called and cannot call out, overriding every other setting. */
  videoCallsDisabled: boolean;
}

/** Decrypted identity for one resident, returned only by the PHI reveal
 *  endpoint (gated by `canViewPhi`; every call is audited server-side). */
export interface CarePhiReveal {
  name: string;
  dob: string | null;
  mrn: string | null;
}

/** A care audit-trail row (compliance export). Mirrors the server `AuditRecord`. */
export interface CareAuditRecord {
  id: string;
  premiseId: string | null;
  actor: string;
  action: string;
  resourceType: string;
  resourceId: string | null;
  /** The resident (`personRef`) this row is about, when there is exactly one. */
  subjectRef: string | null;
  phi: boolean;
  outcome: "success" | "denied" | "error";
  context: Record<string, unknown> | null;
  at: string;
  /** Position in the premise's tamper-evidence hash chain. */
  seq: number;
  hash: string;
}

/** Filters shared by the audit viewer and the CSV export. */
export interface CareAuditFilter {
  /** ISO datetime, inclusive lower bound. */
  from?: string;
  /** ISO datetime, exclusive upper bound. */
  to?: string;
  /** One resident's access log. */
  subjectRef?: string;
  /** One member of staff's activity. */
  actor?: string;
  outcome?: "success" | "denied" | "error";
  phiOnly?: boolean;
}

export interface CareAuditPage {
  rows: CareAuditRecord[];
  /** True when more rows matched than were returned - surface this. */
  truncated: boolean;
  /** Pass back as `beforeSeq` to fetch the next page. */
  nextBeforeSeq: number | null;
}

/** Verdict of a hash-chain walk - "has this trail been altered?". */
export interface CareAuditVerification {
  ok: boolean;
  checked: number;
  /**
   * Rows in range that predate the hash chain and carry no digest. Not a
   * finding - nothing tampered with them - but not covered by the verdict
   * either, so the panel says so rather than letting "intact" imply more than
   * it proves.
   */
  unchained: number;
  headSeq: number;
  headHash: string;
  breakAt: {
    seq: number;
    reason:
      | "missing_row"
      | "hash_mismatch"
      | "prev_mismatch"
      /** The trail extends past the last row we hold - rows removed from the tip. */
      | "truncated_head"
      /** The reader could not restore chain order; NOT a finding about the chain. */
      | "reorder_window_exceeded";
    at: string | null;
  } | null;
}

/** Serialise audit filters into a querystring (shared by viewer + export). */
function careAuditQuery(
  opts: CareAuditFilter & { limit?: number; beforeSeq?: number }
): string {
  const qs = new URLSearchParams();
  if (opts.from) qs.set("from", opts.from);
  if (opts.to) qs.set("to", opts.to);
  if (opts.subjectRef) qs.set("subjectRef", opts.subjectRef);
  if (opts.actor) qs.set("actor", opts.actor);
  if (opts.outcome) qs.set("outcome", opts.outcome);
  if (opts.phiOnly) qs.set("phiOnly", "1");
  if (opts.limit) qs.set("limit", String(opts.limit));
  if (typeof opts.beforeSeq === "number") {
    qs.set("beforeSeq", String(opts.beforeSeq));
  }
  return qs.toString() ? `?${qs}` : "";
}
import type { Role } from "@shared/types/auth";

/** A screen seen from the customer level, so it knows where it lives. */
export interface CompanyScreenRecord extends PremiseScreen {
  premiseId: string;
  premiseName: string;
}

export interface ScreenRecord extends PremiseScreen {
  /** Live heartbeat snapshot if the TV has reported in this session. */
  live?: HeartbeatSnapshot;
}

/** Result of a Fleet bulk action - now backed by a rollout, so it returns the
 *  rollout's id/status for the staged-delivery follow-up on the Rollouts page. */
export interface BulkActionResult {
  ok: true;
  count: number;
  rolloutId?: string;
  status?: RolloutStatus;
}

export interface PremiseSummary {
  premiseId: string;
  /** Mongo `_id` as a string. Needed when assigning users to specific
   *  premises - `User.premises[]` stores ObjectIds, not slugs. */
  mongoId: string | null;
  premiseName: string;
  /** Business segment - drives the Hotel/Care badge in the list. */
  segment: "hotel" | "care";
  /** Tenant ref (Company._id as string), or null for unassigned. */
  companyId: string | null;
  version: number;
  updatedAt: string;
  tileCount: number;
  roomCount: number;
  pageCount: number;
  screenCount: number;
  onlineCount: number;
  models: string[];
  /** License seat summary - drives the "Licenses" column in the list. */
  licenses?: {
    totalSeats: number;
    unlimited: boolean;
    activatedCount: number;
    demoCount: number;
    hasFreeSeats: boolean;
  };
  /** Rolled-up device health - drives the traffic-light chip in the list. */
  health?: PremiseHealth;
  /** Products this premise holds an active license for (License.product). */
  products?: string[];
}

export interface CompanySummary {
  id: string;
  name: string;
  type: string;
  parent: string | null;
  upsalesId: string | null;
}

export interface PhotoMailboxInfo {
  premiseId: string;
  roomId: string;
  slug: string;
  emailAddress: string;
  allowlist: string[];
  retentionDays: number;
  /** When false, new inbound emails are rejected; existing photos stay. */
  active: boolean;
  /** When true, inbound photos require staff approval before reaching the TV. */
  moderated: boolean;
}

export async function unwrap<T>(p: Promise<Response>): Promise<T> {
  const res = await p;
  if (res.status === 401) {
    // The session expired or never existed. AuthContext listens for this
    // event and clears the cached user, which trips RequireAuth → /login.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("foyer:unauthorized"));
    }
    throw new Error("unauthorized");
  }
  if (!res.ok) {
    // Attach the server's structured error code so callers can map it to a
    // friendly, field-level message instead of showing a raw status - plus the
    // whole body, because a refusal is increasingly a REASON and not just a
    // code (the asset delete guard answers 409 with the list of things holding
    // the file, and dropping that would leave the operator with "conflict").
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      [k: string]: unknown;
    };
    throw Object.assign(
      new Error(body.error ?? `${res.status} ${res.statusText}`),
      { code: body.error, status: res.status, body }
    );
  }
  return (await res.json()) as T;
}

/**
 * Which media layer a call addresses. Mirrors the server's `AssetLayer`.
 *
 *   undefined / {kind:"global"}  - the shared Global media library
 *   {kind:"company"}             - one customer's library, shared by its locations
 *   {kind:"premise"}             - one location's own files
 */
export type MediaLayer =
  | { kind: "global" }
  | { kind: "company"; companyId: string }
  | { kind: "premise"; premiseId: string };

export const GLOBAL_MEDIA_LAYER: MediaLayer = { kind: "global" };

/** Base path for the premise-scoped signage routes. */
function signageBase(premiseId: string, suffix: string): string {
  return `/api/foyer/premise/${encodeURIComponent(premiseId)}/signage/${suffix}`;
}

/**
 * Signage scope: one location, a whole customer, or the platform itself.
 *
 * The three route families are identical in shape and differ only in which
 * media layer a write lands on, so callers that work on any of them take this
 * rather than a set of optional ids that could all be set at once.
 *
 * `global` is Nenda's own layer - the curated CHANNELS every customer can
 * schedule but none can edit (`BE-40`). Only an admin session may address it;
 * everyone else reads the same rows through their own scope's list, tagged
 * `scope: "global"`.
 */
export type SignageScopeRef =
  | { kind: "premise"; premiseId: string }
  | { kind: "company"; companyId: string }
  | { kind: "global" };

export function signageScopeBase(
  scope: SignageScopeRef,
  suffix: string
): string {
  if (scope.kind === "premise") return signageBase(scope.premiseId, suffix);
  if (scope.kind === "global") return `/api/foyer/global/signage/${suffix}`;
  return `/api/foyer/company/${encodeURIComponent(scope.companyId)}/signage/${suffix}`;
}

/**
 * What the embeddability probe answers. Mirrors the server's `SignageUrlProbe`.
 *
 * `unknown` is a first-class answer, not an error: a site that was down, slow,
 * or hostile to our user agent tells us nothing about whether a panel on the
 * hotel's own network could frame it. The editor says so and lets the operator
 * proceed - the only thing a probe may block is nothing at all.
 */
export interface SignageUrlProbe {
  embeddable: "yes" | "no" | "unknown";
  status?: number;
  /** The header that refused, verbatim, so the warning can quote it. */
  reason?: string;
  finalUrl?: string;
  /** http:// - a https-served panel blocks it as mixed content. */
  insecure?: boolean;
}

/** What `channel-options` answers. Mirrors the server's `SignageChannelOptions`. */
export interface SignageChannelOptions {
  channels: SignageChannelOption[];
  /** Did this place buy Signage AND Streaming TV? Always true off-premise. */
  liveTvEnabled: boolean;
}

/** The identity part of a scope, for a React dependency key. */
export function signageScopeKey(scope: SignageScopeRef): string {
  if (scope.kind === "premise") return scope.premiseId;
  if (scope.kind === "company") return scope.companyId;
  return "global";
}

/**
 * One live TV channel a signage screen can be pointed at (`BE-41`).
 *
 * Answered by the signage scope's own `channel-options` route rather than by
 * `/distribution/channels`: that one is gated on `canManageDistribution`, the
 * right to edit the catalogue, which a hotel's signage operator has no business
 * holding.
 */
export interface SignageChannelOption {
  contentAssetId: string;
  name: string;
  providerName: string | null;
  logoUrl: string | null;
}

/**
 * Which layer a room-offer call addresses (`POS-5`).
 *
 * Two members, not three: there is no CMS surface for Nenda's own global
 * starter catalogue yet, and a scope the UI cannot reach is a scope nobody
 * tests. Global rows are still READ — they arrive inherited in a premise's
 * list, tagged `scope: "global"` — they just cannot be written from here.
 *
 * Deliberately its own type rather than a reuse of {@link SignageScopeRef}:
 * the two families share a shape today and share no routes, and one type over
 * both would make it a compile-time success to hand a signage scope to an
 * offers call.
 */
export type OfferScopeRef =
  | { kind: "premise"; premiseId: string }
  | { kind: "company"; companyId: string };

function offerCatalogueBase(scope: OfferScopeRef): string {
  return scope.kind === "premise"
    ? `/api/foyer/premise/${encodeURIComponent(scope.premiseId)}/offers/catalogue`
    : `/api/foyer/company/${encodeURIComponent(scope.companyId)}/offers/catalogue`;
}

/** Where one property's deltas live (`POS-14`). Premise-layer only, by design. */
function offerOverrideBase(premiseId: string): string {
  return `/api/foyer/premise/${encodeURIComponent(premiseId)}/offers/overrides`;
}

/** The identity part of an offer scope, for a React dependency key. */
export function offerScopeKey(scope: OfferScopeRef): string {
  return scope.kind === "premise" ? scope.premiseId : scope.companyId;
}

/**
 * What a create or a patch may state.
 *
 * The layer fields and the timestamps are absent on purpose: which layer a row
 * lives on comes from the URL, never from the body, so a hotel cannot post
 * itself a company-layer offer. `scope` is derived server-side from the two ids
 * and echoed back.
 */
export type RoomOfferBody = Partial<
  Omit<
    RoomOffer,
    "id" | "scope" | "premiseId" | "companyId" | "createdAt" | "updatedAt"
  >
>;

/** A JSON request init - the same three lines repeated across every mutation. */
export function jsonBody(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };
}

/** Base path for asset routes - one prefix per layer. */
function assetBase(layer?: MediaLayer): string {
  if (layer?.kind === "premise")
    return `/api/foyer/premise/${encodeURIComponent(layer.premiseId)}/assets`;
  if (layer?.kind === "company")
    return `/api/foyer/company/${encodeURIComponent(layer.companyId)}/assets`;
  return "/api/foyer/assets";
}

/**
 * One ingest, as the panel reads it (`BE-17`). Dates are ISO strings; every
 * field is the job row's own, so the screen cannot show a phase the machine
 * does not believe in.
 */
export interface IngestJobView {
  id: string;
  assetId: string;
  sourceObjectName: string;
  profile: string;
  phase: IngestPhase;
  phaseStartedAt: string;
  deadlineAt: string | null;
  attempts: number;
  progress: number | null;
  error: string | null;
  encoreJobId: string | null;
  manifestUrls: { dash?: string; hls?: string } | null;
  phaseLog: IngestPhaseEntry[];
  incidents: IngestIncident[];
  batchId: string | null;
  /** Package ids the title joins on publish. Empty = nobody sees it. */
  entitlements: string[];
  /**
   * Subtitle files attached to this ingest (`CONTENT-21`).
   *
   * `bytes` rather than a boolean: "uploaded" is a claim, a size is evidence,
   * and a zero-byte subtitle is the failure worth naming.
   */
  subtitles: Array<{
    language: string;
    narrowedFrom: string | null;
    bytes: number | null;
  }>;
  /** Whether the subtitle question has been ANSWERED — not whether files exist. */
  subtitlesDecided?: boolean;
  /**
   * Why this job is not being handed to the farm, or null.
   *
   * Read on every job view rather than fetched separately: a queued job drawn
   * without the sentence explaining it is the shape of bug that gets a working
   * system escalated.
   */
  hold: { reason: IngestHoldReason; since: string } | null;
  createdBy: string;
  createdAt: string;
  completedAt: string | null;
}

/** Whether Foyer can hand a job to the shared farm at all, and why not. */
export interface IngestReadiness {
  ready: boolean;
  profile: string;
  missing: string[];
  explanation: string | null;
}

/**
 * The ingest calls, bound to a surface (`CONTENT-9`).
 *
 * The staff panel and the supplier shell speak to the SAME handlers under two
 * prefixes (`/distribution` and `/owner`), so binding the base once is what
 * stops a supplier call from being written against a staff URL by accident —
 * which would not fail loudly, it would 403 in a place the shell has no way to
 * explain.
 *
 * Both reads carry `readiness` with them on purpose: no caller may draw a
 * queued job without the sentence that says whether it is waiting politely or
 * waiting because Foyer cannot dispatch at all.
 */
function ingestCalls(area: "distribution" | "owner") {
  const root = `/api/foyer/${area}`;
  return {
    listIngestJobs(
      assetId: string
    ): Promise<{ jobs: IngestJobView[]; readiness: IngestReadiness }> {
      return unwrap(
        fetch(`${root}/assets/${encodeURIComponent(assetId)}/ingest`)
      );
    },

    getIngestJob(
      jobId: string
    ): Promise<{ job: IngestJobView; readiness: IngestReadiness }> {
      return unwrap(fetch(`${root}/ingest/${encodeURIComponent(jobId)}`));
    },

    ingestUploadUrl(
      jobId: string
    ): Promise<{ uploadUrl: string; blobName: string; expiresAt: string }> {
      return unwrap(
        fetch(`${root}/ingest/${encodeURIComponent(jobId)}/upload-url`, {
          method: "POST",
        })
      );
    },

    ingestUploaded(jobId: string): Promise<{
      job: IngestJobView;
      bytes: number;
      readiness: IngestReadiness;
    }> {
      return unwrap(
        fetch(`${root}/ingest/${encodeURIComponent(jobId)}/uploaded`, {
          method: "POST",
        })
      );
    },

    /** Phase-aware: the server resumes from whatever failed (`CONTENT-7`). */
    retryIngest(
      jobId: string
    ): Promise<{ job: IngestJobView; readiness: IngestReadiness }> {
      return unwrap(
        fetch(`${root}/ingest/${encodeURIComponent(jobId)}/retry`, {
          method: "POST",
        })
      );
    },

    startIngest(
      assetId: string,
      body: { extension: string; profile?: string; entitlements?: string[] }
    ): Promise<{ job: IngestJobView; readiness: IngestReadiness }> {
      return unwrap(
        fetch(`${root}/assets/${encodeURIComponent(assetId)}/ingest`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        })
      );
    },

    /**
     * Where to PUT one subtitle file (`CONTENT-21`, surfaced by `BE-32`).
     *
     * The answer may narrow the language — a studio ships `film.pt-BR.srt` and
     * the catalogue's column is `VarChar(3)` — and it says so in
     * `narrowedFrom`, because a supplier who is not told sends both files.
     */
    ingestSubtitleUrl(
      jobId: string,
      body: { language: string; extension: string }
    ): Promise<{
      uploadUrl: string;
      blobName: string;
      expiresAt: string;
      language: string;
      narrowedFrom?: string;
    }> {
      return unwrap(
        fetch(`${root}/ingest/${encodeURIComponent(jobId)}/subtitle-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        })
      );
    },

    /**
     * The ladder for one title: what is done, what is outstanding, and why.
     *
     * Answers before an ingest exists — a wizard has to draw step one before
     * there is anything to draw it from.
     */
    vodWizard(assetId: string): Promise<{
      steps: VodWizardStepResult[];
      missing: VodWizardStep[];
      job: IngestJobView | null;
      readiness: IngestReadiness;
    }> {
      return unwrap(
        fetch(`${root}/assets/${encodeURIComponent(assetId)}/wizard`)
      );
    },

    /**
     * "That is all the subtitles" — an ANSWER, not a file.
     *
     * Answered with zero files is answered, and that is the whole point: Ålcom
     * cannot take a subtitle added after the package is registered, so "none
     * yet" and "none at all" are opposite facts that look identical in storage.
     */
    /**
     * "That is all the subtitles" — or, with `false`, "no it is not".
     *
     * Defaults to `true` so every existing caller keeps its meaning. The
     * server refuses a retraction once the ingest has left `queued`.
     */
    ingestSubtitlesDone(
      jobId: string,
      decided = true
    ): Promise<{ job: IngestJobView }> {
      return unwrap(
        fetch(`${root}/ingest/${encodeURIComponent(jobId)}/subtitles-done`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ decided }),
        })
      );
    },

    /**
     * Hand the submission in.
     *
     * Rejects with `submission_incomplete` (409) naming every outstanding step,
     * which is the answer the uploader can act on — unlike "a human will look
     * at this eventually" when the real problem is a missing file.
     */
    submitIngest(jobId: string): Promise<{
      job: IngestJobView;
      steps: VodWizardStepResult[];
      dispatch: boolean;
      readiness: IngestReadiness;
    }> {
      return unwrap(
        fetch(`${root}/ingest/${encodeURIComponent(jobId)}/submit`, {
          method: "POST",
        })
      );
    },

    /**
     * Which packages this ingest joins on publish.
     *
     * ⚠️ Refused on the owner surface — a supplier does not choose which
     * packages carry their title; screening writes the pin.
     */
    setIngestEntitlements(
      jobId: string,
      entitlements: string[]
    ): Promise<{ job: IngestJobView }> {
      return unwrap(
        fetch(`${root}/ingest/${encodeURIComponent(jobId)}/entitlements`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entitlements }),
        })
      );
    },

    /**
     * A short-lived URL a REVIEWER can play the raw master from.
     *
     * ⚠️ The answer is a SAS URL, which is a CREDENTIAL — the signature is in
     * the query string and anybody holding it downloads an unreleased master
     * with no login. Put it in a `<video src>` and let it expire; never log it,
     * never store it, never paste it into a ticket.
     *
     * Staff only. The owner surface answers 404.
     */
    ingestPreview(jobId: string): Promise<{ url: string; expiresAt: string }> {
      return unwrap(
        fetch(`${root}/ingest/${encodeURIComponent(jobId)}/preview`)
      );
    },

    /** The bytes landed — verified server-side, exactly like the master's. */
    ingestSubtitleUploaded(
      jobId: string,
      language: string
    ): Promise<{ job: IngestJobView; bytes: number }> {
      return unwrap(
        fetch(`${root}/ingest/${encodeURIComponent(jobId)}/subtitle-uploaded`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ language }),
        })
      );
    },
  };
}

/**
 * Bulk-ingest batches, on either surface (`BE-31`, `BE-36`).
 *
 * Same handlers, same wire shapes; the base is the whole difference, and the
 * server derives the owner from the session rather than the path.
 *
 * 🚨 `setBatchPackages` is deliberately NOT here. It is registered only on the
 * staff surface (`if (!owned)` in `bulkIngest.ts`): an owner has no package
 * picker because the pin is written at screening, and a route that 403s on
 * every call is a route that should not exist. A supplier-facing screen that
 * offered one would be promising a control that cannot work.
 */
function batchCalls(area: "distribution" | "owner") {
  const root = `/api/foyer/${area}/batches`;
  return {
    listBatches(): Promise<{
      batches: BatchView[];
      readiness: IngestReadiness;
    }> {
      return unwrap(fetch(root));
    },

    getBatch(id: string): Promise<{
      batch: BatchView;
      readiness: IngestReadiness;
    }> {
      return unwrap(fetch(`${root}/${encodeURIComponent(id)}`));
    },

    createBatch(body: {
      name: string;
      entitlements?: string[];
    }): Promise<{ batch: BatchView }> {
      return unwrap(fetch(root, jsonBody("POST", body)));
    },

    batchUploadUrl(
      id: string,
      path: string
    ): Promise<{ uploadUrl: string; blobName: string; expiresAt: string }> {
      return unwrap(
        fetch(
          `${root}/${encodeURIComponent(id)}/upload-url`,
          jsonBody("POST", { path })
        )
      );
    },

    scanBatch(
      id: string
    ): Promise<{ batch: BatchView; verdict: PackageVerdict }> {
      return unwrap(
        fetch(`${root}/${encodeURIComponent(id)}/scan`, { method: "POST" })
      );
    },

    startBatch(id: string): Promise<{
      batch: BatchView | null;
      result: { started: number; blocked: number; refused: number };
    }> {
      return unwrap(
        fetch(`${root}/${encodeURIComponent(id)}/start`, { method: "POST" })
      );
    },

    setBatchIntent(
      id: string,
      action: "pause" | "resume" | "cancel"
    ): Promise<{ batch: BatchView | null }> {
      return unwrap(
        fetch(`${root}/${encodeURIComponent(id)}/${action}`, {
          method: "POST",
        })
      );
    },

    /**
     * Throw an unsent delivery away, bytes included (`ART-7`).
     *
     * Distinct from `cancel`, and the difference is not cosmetic: cancelling a
     * STARTED batch stops it and releases its assets, leaving the record of
     * what happened. This is for a draft that should never have existed — the
     * empty one you get from picking a file the scanner cannot read — where the
     * record is the problem.
     */
    deleteBatch(id: string): Promise<{ deleted: true; blobsRemoved: number }> {
      return unwrap(
        fetch(`${root}/${encodeURIComponent(id)}`, { method: "DELETE" })
      );
    },
  };
}

/** The supplier shell's own batch calls. Same handlers, owner-scoped. */
export const ownerBatchApi = batchCalls("owner");

/** The supplier shell's own ingest calls. Same handlers, owner-scoped. */
export const ownerIngestApi = ingestCalls("owner");

/**
 * The content-analytics calls, bound to a surface (`ANALYTICS-3`).
 *
 * Same reasoning as {@link ingestCalls}: one route set answers under
 * `/analytics/content` for staff and `/owner/analytics` for a supplier, and a
 * supplier call written against the staff URL would not fail loudly — it would
 * 403 where the shell cannot explain it. Binding the base once removes the
 * possibility.
 *
 * ⚠️ `supplier` is a STAFF-ONLY axis. The owner surface refuses it with
 * `filter_not_supported_here` rather than ignoring it, so this signature does
 * not quietly accept one either.
 */
function contentAnalyticsCalls(area: "analytics/content" | "owner/analytics") {
  const root = `/api/foyer/${area}`;
  const qs = (filters: ContentAnalyticsFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.days) params.set("days", String(filters.days));
    if (filters.country) params.set("country", filters.country);
    if (filters.region) params.set("region", filters.region);
    if (filters.propertyType) params.set("propertyType", filters.propertyType);
    if (filters.channelAssetId) params.set("channel", filters.channelAssetId);
    if (filters.categoryId) params.set("category", filters.categoryId);
    if (filters.supplierId) params.set("supplier", filters.supplierId);
    const out = params.toString();
    return out ? `?${out}` : "";
  };
  return {
    contentPlayOverview(
      filters?: ContentAnalyticsFilters
    ): Promise<ContentAnalyticsOverview> {
      return unwrap(fetch(`${root}/overview${qs(filters)}`));
    },
    contentPlayAssets(
      filters?: ContentAnalyticsFilters
    ): Promise<AssetPerspective> {
      return unwrap(fetch(`${root}/assets${qs(filters)}`));
    },
    contentPlayPackages(
      filters?: ContentAnalyticsFilters
    ): Promise<PackagePerspective> {
      return unwrap(fetch(`${root}/packages${qs(filters)}`));
    },
    contentPlaySuppliers(
      filters?: ContentAnalyticsFilters
    ): Promise<SupplierPerspective> {
      return unwrap(fetch(`${root}/suppliers${qs(filters)}`));
    },
    contentPlayChannels(
      filters?: ContentAnalyticsFilters
    ): Promise<ChannelPerspective> {
      return unwrap(fetch(`${root}/channels${qs(filters)}`));
    },
    contentPlayAxes(
      filters?: ContentAnalyticsFilters
    ): Promise<ContentAnalyticsAxes> {
      return unwrap(fetch(`${root}/axes${qs(filters)}`));
    },
  };
}

/** A supplier's own play analytics. Same handlers, owner-scoped. */
export const ownerAnalyticsApi = contentAnalyticsCalls("owner/analytics");

/** Nenda's view of the same numbers, across every supplier. */
export const staffContentAnalyticsApi =
  contentAnalyticsCalls("analytics/content");

/** What the browser knows about a picture that the server cannot work out. */
export interface TitleImageMeta {
  language: string;
  type: ImageType;
  /** Measured from the decoded <img>. The server has no decoder. */
  width?: number | null;
  height?: number | null;
  /**
   * Replace THIS row's picture rather than appending another (`ART-8`).
   *
   * Null or absent appends, which is what a first upload wants. Set it and the
   * server rewrites the named row in place and answers 200 instead of 201 —
   * the difference matters to a panel that is editing a list, because
   * appending where the operator asked for a replacement silently doubles the
   * artwork on a table that has no unique key to notice.
   */
  replace?: string | null;
}

/**
 * Artwork upload, written ONCE and bound to a surface by its two callers
 * (`ART-2`).
 *
 * The same reason `ingestCalls(area)` exists: the staff panel and the supplier
 * shell speak to the same handlers under `/distribution` and `/owner`, and a
 * supplier call written against a staff URL would not fail loudly — it would
 * 403 somewhere the shell has no way to explain.
 *
 * ⚠️ The scalars ride in the QUERY STRING, matching `uploadAsset` and the
 * server's `uploadMeta`: `req.file()` consumes the stream as it arrives and
 * sibling field ordering is not guaranteed, so reading them as multipart
 * fields would mean buffering the whole body first. No `content-type` header —
 * the browser must set the multipart boundary itself.
 */
function uploadTitleImage(
  area: "distribution" | "owner",
  idOrReference: string,
  file: File,
  meta: TitleImageMeta
): Promise<VodTitleRow> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  const qs = new URLSearchParams({ language: meta.language, type: meta.type });
  if (meta.width) qs.set("w", String(meta.width));
  if (meta.height) qs.set("h", String(meta.height));
  if (meta.replace) qs.set("replace", meta.replace);
  return unwrap<VodTitleRow>(
    fetch(
      `/api/foyer/${area}/vod-titles/${encodeURIComponent(
        idOrReference
      )}/images?${qs}`,
      { method: "POST", body: fd }
    )
  );
}

/**
 * What a customer can watch right now (`WL-3`).
 *
 * Derived from live grants on every request and cached nowhere — a stored copy
 * would be a second answer to a question that already has one, and wrong from
 * the first expiry onwards with nobody having written to it.
 */
export interface StorefrontAccess {
  assetIds: string[];
  grants: { id: string; assetIds: string[]; expiresAt: string | null }[];
}

export const foyerApi = {
  ...ingestCalls("distribution"),
  ...batchCalls("distribution"),

  /**
   * Which packages a batch's titles are pinned into — STAFF ONLY.
   *
   * Not on `batchCalls` because the server registers it only on the staff
   * surface: an owner has no package picker, since the pin is written at
   * screening. Keeping it out of the factory is what stops a supplier screen
   * from growing a control that would 403 on every click.
   */
  setBatchPackages(
    id: string,
    entitlements: string[]
  ): Promise<{ batch: BatchView }> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/batches/${encodeURIComponent(id)}/packages`,
        jsonBody("POST", { entitlements })
      )
    );
  },

  listPremises(): Promise<PremiseSummary[]> {
    return unwrap<PremiseSummary[]>(fetch("/api/foyer/premises"));
  },

  getPremise(premiseId: string): Promise<PremiseConfig> {
    return unwrap<PremiseConfig>(fetch(`/api/foyer/premise/${premiseId}`));
  },

  putPremise(premiseId: string, cfg: PremiseConfig): Promise<PremiseConfig> {
    return unwrap<PremiseConfig>(
      fetch(`/api/foyer/premise/${premiseId}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(cfg),
      })
    );
  },

  // ── AI drafting (docs/ai-content-generation.md) ────────────────────────
  //
  // There is deliberately no "apply" call here. Accepted fields are folded into
  // the workspace draft by `applyAccepted` and written by `putPremise` above,
  // from the Save bar - so a review that is never saved changes nothing.

  aiAvailability(): Promise<
    { available: true; kind: string } | { available: false; reason: string }
  > {
    return unwrap(fetch("/api/foyer/ai/availability"));
  },

  translationCoverage(premiseId: string): Promise<{
    defaultLocale: string;
    coverage: Array<{ locale: string; filled: number; translatable: number }>;
  }> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/translation-coverage`)
    );
  },

  startTranslation(premiseId: string): Promise<ContentProposal> {
    return unwrap<ContentProposal>(
      fetch(`/api/foyer/premise/${premiseId}/content-proposals/translation`, {
        method: "POST",
      })
    );
  },

  listProposals(premiseId: string): Promise<{ proposals: ContentProposal[] }> {
    return unwrap(fetch(`/api/foyer/premise/${premiseId}/content-proposals`));
  },

  getProposal(id: string): Promise<ContentProposal> {
    return unwrap<ContentProposal>(fetch(`/api/foyer/content-proposals/${id}`));
  },

  decideProposalField(args: {
    id: string;
    fieldId: string;
    decision: FieldDecision;
    edited?: string;
  }): Promise<ContentProposal> {
    return unwrap<ContentProposal>(
      fetch(
        `/api/foyer/content-proposals/${args.id}/fields/${encodeURIComponent(
          args.fieldId
        )}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            decision: args.decision,
            edited: args.edited,
          }),
        }
      )
    );
  },

  acceptSourcedFields(id: string): Promise<ContentProposal> {
    return unwrap<ContentProposal>(
      fetch(`/api/foyer/content-proposals/${id}/accept-sourced`, {
        method: "POST",
      })
    );
  },

  markProposalApplied(
    id: string,
    appliedIds: string[]
  ): Promise<ContentProposal> {
    return unwrap<ContentProposal>(
      fetch(`/api/foyer/content-proposals/${id}/applied`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ appliedIds }),
      })
    );
  },

  discardProposal(id: string): Promise<ContentProposal> {
    return unwrap<ContentProposal>(
      fetch(`/api/foyer/content-proposals/${id}/discard`, { method: "POST" })
    );
  },

  sendMessage(args: {
    premiseId: string;
    roomId: string;
    title?: string;
    body: string;
    durationMs?: number;
  }): Promise<{ ok: true }> {
    const { premiseId, roomId, ...payload } = args;
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/premise/${premiseId}/room/${roomId}/message`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      })
    );
  },

  // ── Instagram connection (Graph API) ────────────────────────────────

  /** Current Instagram connection status for a premise. */
  instagramStatus(premiseId: string): Promise<InstagramConnectionStatus> {
    return unwrap<InstagramConnectionStatus>(
      fetch(`/api/foyer/premise/${premiseId}/instagram/status`)
    );
  },

  /** Disconnect the premise's Instagram account (clears the stored token). */
  disconnectInstagram(premiseId: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/premise/${premiseId}/instagram`, { method: "DELETE" })
    );
  },

  /** URL the CMS opens in a popup to begin the OAuth connect flow. */
  instagramConnectUrl(premiseId: string): string {
    return `/api/foyer/instagram/auth/start?premiseId=${encodeURIComponent(
      premiseId
    )}`;
  },

  // ── Food menu (Matsedel) RSS ────────────────────────────────────────

  /** Fetch + parse a candidate menu feed WITHOUT saving it, so the operator can
   *  see exactly what we made of their URL before committing to it. Rejects
   *  with `err.code` set to a `menu_feed_*` reason. */
  previewMenuFeed(premiseId: string, url: string): Promise<MenuFeedResult> {
    return unwrap<MenuFeedResult>(
      fetch(`/api/foyer/premise/${premiseId}/menu-feed/preview`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url }),
      })
    );
  },

  /** The premise's currently-configured feed, as the TV sees it. */
  getMenuFeed(premiseId: string): Promise<MenuFeedResult> {
    return unwrap<MenuFeedResult>(
      fetch(`/api/foyer/premise/${premiseId}/menu-feed`)
    );
  },

  // ── VilMer activities ───────────────────────────────────────────────

  /** Try a VilMer API address and a room token WITHOUT saving either, so an
   *  operator sees the real activities before committing. `usingFixtures` is
   *  true when no URL was supplied and the stand-in answered - the panel must
   *  say so rather than showing a green tick for data we invented. Rejects with
   *  `err.code` set to a `vilmer_*` reason. */
  previewVilmer(
    premiseId: string,
    body: { url?: string; roomId?: string; token?: string }
  ): Promise<VilmerActivitiesResult & { usingFixtures?: boolean }> {
    return unwrap<VilmerActivitiesResult & { usingFixtures?: boolean }>(
      fetch(`/api/foyer/premise/${premiseId}/vilmer/preview`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      })
    );
  },

  // Asset routes are layer-aware; `assetBase` picks the prefix. Reads on a
  // premise layer return that location's files PLUS its company's (marked
  // `scope: "company"`), unless `ownOnly` asks for just its own. Writes always
  // land in exactly the layer addressed.

  listAssets(
    layer?: MediaLayer,
    opts?: { ownOnly?: boolean }
  ): Promise<AssetSummary[]> {
    const q = opts?.ownOnly && layer?.kind === "premise" ? "?scope=own" : "";
    return unwrap<AssetSummary[]>(fetch(`${assetBase(layer)}${q}`));
  },

  uploadAsset(
    file: File,
    folder?: string,
    layer?: MediaLayer,
    /** Display name + browser-measured shape. See the server's uploadMeta. */
    meta?: {
      name?: string;
      width?: number | null;
      height?: number | null;
      durationSeconds?: number | null;
      posterDataUrl?: string | null;
      /**
       * Automatic purge date as `YYYY-MM-DD` (BE-29), or null for "keep it".
       *
       * Omitted entirely by the bare drag-and-drop upload path, deliberately -
       * a purge date is a decision, and a route with nowhere to make it must
       * not invent one.
       */
      purgeAt?: string | null;
    }
  ): Promise<AssetSummary> {
    const fd = new FormData();
    // The poster goes FIRST, on purpose: @fastify/multipart only exposes
    // fields that were parsed before the file part, so a poster appended after
    // it would silently never arrive.
    if (meta?.posterDataUrl) fd.append("poster", meta.posterDataUrl);
    fd.append("file", file, file.name);
    const qs = new URLSearchParams();
    if (folder) qs.set("folder", folder);
    if (meta?.name) qs.set("name", meta.name);
    if (meta?.width) qs.set("w", String(meta.width));
    if (meta?.height) qs.set("h", String(meta.height));
    if (meta?.durationSeconds) qs.set("d", String(meta.durationSeconds));
    if (meta?.purgeAt) qs.set("purge", meta.purgeAt);
    const q = qs.toString() ? `?${qs}` : "";
    return unwrap<AssetSummary>(
      fetch(`${assetBase(layer)}${q}`, { method: "POST", body: fd })
    );
  },

  /**
   * Delete a file. Throws with `code: "asset_in_use"` and a body naming the
   * holders when something still points at it - pass the error to
   * {@link assetInUseMessage}.
   */
  deleteAsset(filename: string, layer?: MediaLayer): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`${assetBase(layer)}/${encodeURIComponent(filename)}`, {
        method: "DELETE",
      })
    );
  },

  /** What is currently using a file - the same answer the 409 gives, offered
   *  before anybody presses Delete. */
  assetUsage(
    filename: string,
    layer?: MediaLayer
  ): Promise<AssetUsageResponse> {
    return unwrap<AssetUsageResponse>(
      fetch(`${assetBase(layer)}/${encodeURIComponent(filename)}/usage`)
    );
  },

  /**
   * Set or clear a file's automatic purge date (BE-29).
   *
   * `null` clears it. Layer-aware unlike {@link setAssetScope}: scheduling a
   * file's destruction is an ordinary write on the layer that owns it, so a
   * location may do it to its own files and gets a 404 for an inherited one.
   */
  setAssetPurgeAt(
    filename: string,
    purgeAt: string | null,
    layer?: MediaLayer
  ): Promise<{ ok: true; purgeAt: string | null }> {
    return unwrap<{ ok: true; purgeAt: string | null }>(
      fetch(`${assetBase(layer)}/${encodeURIComponent(filename)}/purge-at`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ purgeAt }),
      })
    );
  },

  /**
   * Retry a clip whose panel transcode FAILED ("Ej optimerad").
   *
   * Layer-aware like the delete beside it: a location retries its own clips
   * and gets a 404 for an inherited one. Answers `202` with `state: "queued"`;
   * the library's existing encoding poll carries it to `ready` or `failed`.
   * A clip in any other state is refused with `409 panel_not_failed` - a
   * `ready` one would be re-encoded from an already-encoded file.
   */
  retryPanelTranscode(
    filename: string,
    layer?: MediaLayer
  ): Promise<{ state: "queued"; filename: string }> {
    return unwrap<{ state: "queued"; filename: string }>(
      fetch(
        `${assetBase(layer)}/${encodeURIComponent(filename)}/panel-transcode`,
        { method: "POST" }
      )
    );
  },

  /** Move an asset into another layer (admin). Always the global route - it is
   *  the only one that may reach across layers. */
  setAssetScope(
    filename: string,
    layer: MediaLayer
  ): Promise<{ ok: true; premiseId: string | null; companyId: string | null }> {
    return unwrap<{
      ok: true;
      premiseId: string | null;
      companyId: string | null;
    }>(
      fetch(`/api/foyer/assets/${encodeURIComponent(filename)}/scope`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          premiseId: layer.kind === "premise" ? layer.premiseId : null,
          companyId: layer.kind === "company" ? layer.companyId : null,
        }),
      })
    );
  },

  // ── Media folders (virtual organization over the flat asset store) ──

  listAssetFolders(
    layer?: MediaLayer,
    opts?: { ownOnly?: boolean }
  ): Promise<string[]> {
    const q = opts?.ownOnly && layer?.kind === "premise" ? "?scope=own" : "";
    return unwrap<string[]>(fetch(`${assetBase(layer)}/folders${q}`));
  },

  createAssetFolder(
    folderPath: string,
    layer?: MediaLayer
  ): Promise<{ path: string }> {
    return unwrap<{ path: string }>(
      fetch(`${assetBase(layer)}/folders`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ path: folderPath }),
      })
    );
  },

  deleteAssetFolder(
    folderPath: string,
    layer?: MediaLayer
  ): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`${assetBase(layer)}/folders/delete`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ path: folderPath }),
      })
    );
  },

  moveAssets(
    filenames: string[],
    folder: string,
    layer?: MediaLayer
  ): Promise<{ moved: number }> {
    return unwrap<{ moved: number }>(
      fetch(`${assetBase(layer)}/move`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ filenames, folder }),
      })
    );
  },

  createPairing(args: {
    premiseId: string;
    expiryDays: number;
    createdBy: string;
  }): Promise<PairingCode> {
    return unwrap<PairingCode>(
      fetch(`/api/foyer/premise/${args.premiseId}/pairings`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          expiryDays: args.expiryDays,
          createdBy: args.createdBy,
        }),
      })
    );
  },

  listPairings(premiseId: string): Promise<PairingCode[]> {
    return unwrap<PairingCode[]>(
      fetch(`/api/foyer/premise/${premiseId}/pairings`)
    );
  },

  revokePairing(code: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/pairings/${code}`, { method: "DELETE" })
    );
  },

  listScreens(premiseId: string): Promise<ScreenRecord[]> {
    return unwrap<ScreenRecord[]>(
      fetch(`/api/foyer/premise/${premiseId}/screens`)
    );
  },

  /**
   * Every screen a customer has, across its locations and any sub-companies.
   *
   * Carries the location each screen is in, because at this scope "Reception"
   * is not a name - there is one in every hotel, and the picker has to be able
   * to tell them apart.
   *
   * No live heartbeat: this feeds pickers, not the fleet view.
   */
  listCompanyScreens(companyId: string): Promise<CompanyScreenRecord[]> {
    return unwrap<CompanyScreenRecord[]>(
      fetch(`/api/foyer/company/${encodeURIComponent(companyId)}/screens`)
    );
  },

  /** Set a screen's role - in-room TV vs corridor door sign. */
  setScreenRole(
    premiseId: string,
    screenId: string,
    role: ScreenRole
  ): Promise<{ ok: true; role: ScreenRole }> {
    return unwrap<{ ok: true; role: ScreenRole }>(
      fetch(`/api/foyer/premise/${premiseId}/screens/${screenId}/role`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ role }),
      })
    );
  },

  /**
   * Set a door-sign screen's mount orientation - landscape (default) vs
   * portrait. A per-device layout toggle on the same renderer (door-sign-spec
   * §3), not a separate build.
   */
  setScreenOrientation(
    premiseId: string,
    screenId: string,
    orientation: DoorSignOrientation
  ): Promise<{ ok: true; orientation: DoorSignOrientation }> {
    return unwrap<{ ok: true; orientation: DoorSignOrientation }>(
      fetch(
        `/api/foyer/premise/${premiseId}/screens/${screenId}/door-sign-orientation`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ orientation }),
        }
      )
    );
  },

  /** Rolled-up device health for one premise (premise overview health card). */
  premiseHealth(premiseId: string): Promise<PremiseHealth> {
    return unwrap<PremiseHealth>(
      fetch(`/api/foyer/premise/${premiseId}/health`)
    );
  },

  /** Global app-wide settings (General settings page). */
  getSettings(): Promise<FoyerSettings> {
    return unwrap<FoyerSettings>(fetch(`/api/foyer/settings`));
  },

  updateSettings(patch: Partial<FoyerSettings>): Promise<FoyerSettings> {
    return unwrap<FoyerSettings>(
      fetch(`/api/foyer/settings`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  /** Cross-premise fleet listing (company-scoped server-side). */
  fleetDevices(
    params: {
      companyId?: string;
      premiseId?: string;
      premiseIds?: string[];
      platform?: string;
      status?: string;
      q?: string;
    } = {}
  ): Promise<FleetResponse> {
    const qs = new URLSearchParams();
    if (params.companyId) qs.set("companyId", params.companyId);
    if (params.premiseId) qs.set("premiseId", params.premiseId);
    else if (params.premiseIds)
      qs.set("premiseIds", params.premiseIds.join(","));
    if (params.platform) qs.set("platform", params.platform);
    if (params.status) qs.set("status", params.status);
    if (params.q) qs.set("q", params.q);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return unwrap<FleetResponse>(fetch(`/api/foyer/fleet/devices${suffix}`));
  },

  fleetAnalytics(
    scope: {
      companyId?: string;
      premiseId?: string;
      premiseIds?: string[];
    } = {}
  ): Promise<FleetAnalytics> {
    const qs = new URLSearchParams();
    if (scope.companyId) qs.set("companyId", scope.companyId);
    if (scope.premiseId) qs.set("premiseId", scope.premiseId);
    else if (scope.premiseIds) qs.set("premiseIds", scope.premiseIds.join(","));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return unwrap<FleetAnalytics>(fetch(`/api/foyer/fleet/analytics${suffix}`));
  },

  fleetHistory(
    range: "day" | "week" | "month",
    scope: {
      companyId?: string;
      premiseId?: string;
      premiseIds?: string[];
    } = {}
  ): Promise<FleetHistory> {
    const qs = new URLSearchParams({ range });
    if (scope.companyId) qs.set("companyId", scope.companyId);
    if (scope.premiseId) qs.set("premiseId", scope.premiseId);
    else if (scope.premiseIds) qs.set("premiseIds", scope.premiseIds.join(","));
    return unwrap<FleetHistory>(
      fetch(`/api/foyer/fleet/analytics/history?${qs.toString()}`)
    );
  },

  /**
   * Estimated energy + CO₂ report (ESG). Scope by `premiseId` (one property),
   * `companyId` (a chain rollup), or neither (own company / whole fleet).
   */
  fleetEnergy(
    range: EnergyRange = "week",
    opts: { premiseId?: string; companyId?: string } = {}
  ): Promise<EnergyReport> {
    const qs = new URLSearchParams({ range });
    if (opts.premiseId) qs.set("premiseId", opts.premiseId);
    if (opts.companyId) qs.set("companyId", opts.companyId);
    return unwrap<EnergyReport>(
      fetch(`/api/foyer/fleet/energy?${qs.toString()}`)
    );
  },

  /**
   * Metered-usage pacing report: every metered signage premise, coloured
   * red/yellow/green against its own monthly target. Scope by `companyId`, or
   * neither for own company / whole fleet (mirrors `fleetEnergy`).
   */
  fleetMeteredUsage(
    opts: { companyId?: string } = {}
  ): Promise<MeteredUsageReport> {
    const qs = new URLSearchParams();
    if (opts.companyId) qs.set("companyId", opts.companyId);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return unwrap<MeteredUsageReport>(
      fetch(`/api/foyer/fleet/metered-usage${suffix}`)
    );
  },

  /**
   * Metered-usage KPI, month by month: per premise attainment against its
   * own target plus the fleet pass rate against the goal. Closed months are
   * frozen server-side; the running month is live.
   */
  fleetMeteredUsageMonthly(
    opts: { companyId?: string; months?: number } = {}
  ): Promise<MeteredMonthlyReport> {
    const qs = new URLSearchParams();
    if (opts.companyId) qs.set("companyId", opts.companyId);
    if (opts.months) qs.set("months", String(opts.months));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return unwrap<MeteredMonthlyReport>(
      fetch(`/api/foyer/fleet/metered-usage/monthly${suffix}`)
    );
  },

  // ── OTA / staged rollouts (Phase 4) ──

  listRollouts(): Promise<{ rollouts: Rollout[] }> {
    return unwrap<{ rollouts: Rollout[] }>(fetch(`/api/foyer/ota/rollouts`));
  },

  rollout(
    id: string
  ): Promise<{ rollout: Rollout; adoption: RolloutAdoption }> {
    return unwrap<{ rollout: Rollout; adoption: RolloutAdoption }>(
      fetch(`/api/foyer/ota/rollouts/${id}`)
    );
  },

  createRollout(input: {
    name: string;
    kind: OtaKind;
    targetVersion: string;
    premiseId?: string;
    platform?: DevicePlatform;
    groupId?: string;
    canaryPct?: number;
    artifactUrl?: string;
    fileName?: string;
    sizeBytes?: number;
    ntpServer?: string;
  }): Promise<Rollout> {
    return unwrap<Rollout>(
      fetch(`/api/foyer/ota/rollouts`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  /** Drive the rollout state machine: start | advance | pause | resume | rollback. */
  rolloutAction(
    id: string,
    action: "start" | "advance" | "pause" | "resume" | "rollback"
  ): Promise<Rollout> {
    return unwrap<Rollout>(
      fetch(`/api/foyer/ota/rollouts/${id}/${action}`, { method: "POST" })
    );
  },

  /** Upload an OEM firmware image; it's hosted at /fw/<id>.<ext> and the
   *  returned absolute URL feeds a firmware rollout's artifactUrl. */
  uploadFirmware(file: File): Promise<FirmwareImageSummary> {
    const fd = new FormData();
    fd.append("file", file, file.name);
    return unwrap<FirmwareImageSummary>(
      fetch(`/api/foyer/ota/firmware`, { method: "POST", body: fd })
    );
  },

  // ── Device groups (Phase 5) ──

  listDeviceGroups(): Promise<{ groups: DeviceGroup[] }> {
    return unwrap<{ groups: DeviceGroup[] }>(fetch(`/api/foyer/device-groups`));
  },

  createDeviceGroup(input: {
    name: string;
    description?: string;
    screenIds?: string[];
  }): Promise<DeviceGroup> {
    return unwrap<DeviceGroup>(
      fetch(`/api/foyer/device-groups`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  updateDeviceGroup(
    id: string,
    patch: { name?: string; description?: string; screenIds?: string[] }
  ): Promise<DeviceGroup> {
    return unwrap<DeviceGroup>(
      fetch(`/api/foyer/device-groups/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  deleteDeviceGroup(id: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/device-groups/${id}`, { method: "DELETE" })
    );
  },

  /** Reboot every device in a group, routed server-side through a rollout
   *  (staged delivery + ack adoption + audit), like the Fleet bulk actions.
   *  The returned `rolloutId` is then driven from the Rollouts page. */
  rebootGroup(id: string, canaryPct?: number): Promise<BulkActionResult> {
    return unwrap<BulkActionResult>(
      fetch(`/api/foyer/device-groups/${id}/reboot`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ canaryPct }),
      })
    );
  },

  /** Push a reception message to every device in a group (same rollout spine). */
  messageGroup(args: {
    id: string;
    title?: string;
    body: string;
    durationMs?: number;
    canaryPct?: number;
  }): Promise<BulkActionResult> {
    const { id, ...rest } = args;
    return unwrap<BulkActionResult>(
      fetch(`/api/foyer/device-groups/${id}/message`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(rest),
      })
    );
  },

  // ── Power schedules (Phase 5) - premise-scoped ──

  listPowerSchedules(
    premiseId: string
  ): Promise<{ schedules: PowerSchedule[] }> {
    return unwrap<{ schedules: PowerSchedule[] }>(
      fetch(`/api/foyer/premise/${premiseId}/power-schedules`)
    );
  },

  createPowerSchedule(
    premiseId: string,
    input: {
      name: string;
      timezone?: string;
      rules?: PowerScheduleRule[];
      enabled?: boolean;
    }
  ): Promise<PowerSchedule> {
    return unwrap<PowerSchedule>(
      fetch(`/api/foyer/premise/${premiseId}/power-schedules`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  updatePowerSchedule(
    premiseId: string,
    id: string,
    patch: {
      name?: string;
      timezone?: string;
      rules?: PowerScheduleRule[];
      enabled?: boolean;
    }
  ): Promise<PowerSchedule> {
    return unwrap<PowerSchedule>(
      fetch(`/api/foyer/premise/${premiseId}/power-schedules/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  deletePowerSchedule(premiseId: string, id: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/premise/${premiseId}/power-schedules/${id}`, {
        method: "DELETE",
      })
    );
  },

  // ── Health alerting (Phase 5) - rules + fired alerts ──

  listAlertRules(): Promise<{ rules: AlertRule[]; types: HealthAlertType[] }> {
    return unwrap<{ rules: AlertRule[]; types: HealthAlertType[] }>(
      fetch("/api/foyer/alert-rules")
    );
  },

  /** What is actually in force for one premise, per type, with its source tier. */
  effectiveAlertRules(
    premiseId: string
  ): Promise<{ premiseId: string; effective: EffectiveAlertRule[] }> {
    return unwrap<{ premiseId: string; effective: EffectiveAlertRule[] }>(
      fetch(
        `/api/foyer/alert-rules/effective?premiseId=${encodeURIComponent(premiseId)}`
      )
    );
  },

  /**
   * Upsert the rule at one tier target. PUT (not POST) because the server keys
   * on (scope target, type) rather than minting a row per call - saving the
   * same toggle twice must not create a second competing rule.
   */
  saveAlertRule(input: {
    type: HealthAlertType;
    scope: AlertRuleScope;
    companyId?: string;
    premiseId?: string;
    enabled?: boolean;
    threshold?: number | null;
    channels?: AlertChannel[];
    recipients?: string[];
  }): Promise<AlertRule> {
    return unwrap<AlertRule>(
      fetch("/api/foyer/alert-rules", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  /** Clear an override so the target inherits its parent tier again. */
  deleteAlertRule(ruleId: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/alert-rules/${ruleId}`, { method: "DELETE" })
    );
  },

  // ── Staff push opt-in (the `push` alert channel's address book) ──

  /** The VAPID application server key this browser must subscribe with, and
   *  whether the deployment has a real one at all. */
  staffPushKey(): Promise<{ publicKey: string | null; configured: boolean }> {
    return unwrap<{ publicKey: string | null; configured: boolean }>(
      fetch("/api/foyer/staff-push/key")
    );
  },

  /** The caller's own opted-in browsers. Never carries a subscription. */
  staffPushDevices(): Promise<{
    configured: boolean;
    devices: StaffPushDevice[];
  }> {
    return unwrap<{ configured: boolean; devices: StaffPushDevice[] }>(
      fetch("/api/foyer/staff-push/devices")
    );
  },

  /** Register this browser. Idempotent on the endpoint. */
  staffPushSubscribe(input: {
    subscription: PushSubscriptionJSON;
    label?: string;
  }): Promise<{ ok: true; device: StaffPushDevice }> {
    return unwrap<{ ok: true; device: StaffPushDevice }>(
      fetch("/api/foyer/staff-push/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  /** Turn this browser off again. */
  staffPushUnsubscribe(
    endpoint: string
  ): Promise<{ ok: true; removed: boolean }> {
    return unwrap<{ ok: true; removed: boolean }>(
      fetch("/api/foyer/staff-push/unsubscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ endpoint }),
      })
    );
  },

  listAlerts(params?: {
    status?: "open" | "resolved";
    premiseId?: string;
    limit?: number;
  }): Promise<{ alerts: HealthAlertRecord[] }> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.premiseId) qs.set("premiseId", params.premiseId);
    if (params?.limit) qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs}` : "";
    return unwrap<{ alerts: HealthAlertRecord[] }>(
      fetch(`/api/foyer/alerts${suffix}`)
    );
  },

  unpairScreen(premiseId: string, screenId: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/premise/${premiseId}/screens/${screenId}`, {
        method: "DELETE",
      })
    );
  },

  // ── Zero-touch provisioning ──────────────────────────────────────────
  /** List pre-seeded + activated screens for a premise, with seat usage. */
  provisioningList(
    premiseId: string
  ): Promise<{ screens: ScreenRecord[]; seats: ProvisioningSeatSummary }> {
    return unwrap(fetch(`/api/foyer/premise/${premiseId}/provisioning`));
  },

  /** Pre-seed a single device by serial. `room` = Room.id or room name. */
  provisionDevice(
    premiseId: string,
    input: { serial: string; room: string; model?: string; role?: ScreenRole }
  ): Promise<ProvisioningResult> {
    return unwrap<ProvisioningResult>(
      fetch(`/api/foyer/premise/${premiseId}/provisioning`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  /** Bulk pre-seed from a CSV file (serial, room[, model]). */
  provisionImport(premiseId: string, file: File): Promise<ProvisioningResult> {
    const fd = new FormData();
    fd.append("file", file, file.name);
    return unwrap<ProvisioningResult>(
      fetch(`/api/foyer/premise/${premiseId}/provisioning/import`, {
        method: "POST",
        body: fd,
      })
    );
  },

  /** Remove a pre-seeded device that hasn't activated yet. */
  deleteProvisioned(
    premiseId: string,
    screenId: string
  ): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/premise/${premiseId}/provisioning/${screenId}`, {
        method: "DELETE",
      })
    );
  },

  screenStatusLog(screenId: string, days = 14): Promise<ScreenStatusEvent[]> {
    return unwrap<ScreenStatusEvent[]>(
      fetch(`/api/foyer/screens/${screenId}/status-log?days=${days}`)
    );
  },

  /** Grouped uncaught front-end errors, most frequent first (admin only). */
  clientErrors(
    days = 7,
    app?: string
  ): Promise<{ groups: ClientErrorGroup[] }> {
    const q = new URLSearchParams({ days: String(days) });
    if (app) q.set("app", app);
    return unwrap<{ groups: ClientErrorGroup[] }>(
      fetch(`/api/foyer/client-errors?${q.toString()}`)
    );
  },

  /** Recent app / renderer exits a TV pushed on boot, newest first. */
  screenExitEvents(
    premiseId: string,
    screenId: string,
    days = 14
  ): Promise<{ events: DeviceExitEvent[] }> {
    return unwrap<{ events: DeviceExitEvent[] }>(
      fetch(
        `/api/foyer/premise/${premiseId}/screens/${screenId}/exit-events?days=${days}`
      )
    );
  },

  /** The TV's continuously shipped log lines, newest first (DeviceLogPanel). */
  screenLogEntries(
    premiseId: string,
    screenId: string,
    opts: { before?: string; level?: "all" | "problems"; limit?: number } = {}
  ): Promise<DeviceLogPage> {
    const q = new URLSearchParams();
    if (opts.before) q.set("before", opts.before);
    if (opts.level) q.set("level", opts.level);
    if (opts.limit) q.set("limit", String(opts.limit));
    const qs = q.toString();
    return unwrap<DeviceLogPage>(
      fetch(
        `/api/foyer/premise/${premiseId}/screens/${screenId}/log-entries${qs ? `?${qs}` : ""}`
      )
    );
  },

  screenHistory(
    premiseId: string,
    screenId: string,
    limit = 20
  ): Promise<ScreenHistoryEntry[]> {
    return unwrap<ScreenHistoryEntry[]>(
      fetch(
        `/api/foyer/premise/${premiseId}/screens/${screenId}/history?limit=${limit}`
      )
    );
  },

  rebootScreen(
    premiseId: string,
    screenId: string
  ): Promise<{ ok: true; commandId: string }> {
    return unwrap<{ ok: true; commandId: string }>(
      fetch(`/api/foyer/premise/${premiseId}/screens/${screenId}/reboot`, {
        method: "POST",
      })
    );
  },

  /** Ask a device to ping its heartbeat immediately (durable command). */
  refreshTelemetry(
    premiseId: string,
    screenId: string
  ): Promise<{ ok: true; commandId: string }> {
    return unwrap<{ ok: true; commandId: string }>(
      fetch(
        `/api/foyer/premise/${premiseId}/screens/${screenId}/refresh-telemetry`,
        { method: "POST" }
      )
    );
  },

  /**
   * Switch one screen's display on or off now (durable command).
   *
   * The immediate counterpart to a power schedule. Deliberately NOT a state the
   * server records: the device's heartbeat stays the authority on what actually
   * happened, so a panel that cannot switch reports the truth rather than
   * having an operator's intent written over it.
   */
  setScreenPower(
    premiseId: string,
    screenId: string,
    on: boolean
  ): Promise<{ ok: true; commandId: string }> {
    return unwrap<{ ok: true; commandId: string }>(
      fetch(`/api/foyer/premise/${premiseId}/screens/${screenId}/power`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ on }),
      })
    );
  },

  /** Durable-command history for a screen (newest first). */
  screenCommands(screenId: string, limit = 20): Promise<FoyerCommand[]> {
    return unwrap<FoyerCommand[]>(
      fetch(`/api/foyer/screens/${screenId}/commands?limit=${limit}`)
    );
  },

  /** Request an in-app screenshot (durable command; result holds the asset URL). */
  screenshotScreen(
    premiseId: string,
    screenId: string
  ): Promise<{ ok: true; commandId: string }> {
    return unwrap<{ ok: true; commandId: string }>(
      fetch(`/api/foyer/premise/${premiseId}/screens/${screenId}/screenshot`, {
        method: "POST",
      })
    );
  },

  /** Request a recent-log capture (durable command; result holds the asset URL). */
  captureLogs(
    premiseId: string,
    screenId: string
  ): Promise<{ ok: true; commandId: string }> {
    return unwrap<{ ok: true; commandId: string }>(
      fetch(
        `/api/foyer/premise/${premiseId}/screens/${screenId}/capture-logs`,
        { method: "POST" }
      )
    );
  },

  /**
   * Open a short-lived window during which this TV may re-bind from its
   * hardware serial alone. Needed when a device legitimately can't satisfy the
   * automatic second factor on `/screens/resolve` - it was moved, the property
   * changed ISP, or a long-pre-seeded serial is only now being installed.
   * The first successful re-attach consumes it; it expires on its own regardless.
   */
  openReattachWindow(
    premiseId: string,
    screenId: string,
    minutes = 15
  ): Promise<{ ok: true; screenId: string; until: string }> {
    return unwrap<{ ok: true; screenId: string; until: string }>(
      fetch(
        `/api/foyer/premise/${premiseId}/screens/${screenId}/reattach-window`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ minutes }),
        }
      )
    );
  },

  regenerateGuestHotspot(
    premiseId: string,
    screenId: string
  ): Promise<{ ssid: string; password: string }> {
    return unwrap<{ ssid: string; password: string }>(
      fetch(
        `/api/foyer/premise/${premiseId}/screens/${screenId}/guest-casting/regenerate`,
        { method: "POST" }
      )
    );
  },

  /** Bulk reboot, routed server-side through a rollout (staged delivery +
   *  adoption + audit). `canaryPct` < 100 dispatches only that slice first;
   *  the returned `rolloutId` is then driven from the Rollouts page. */
  rebootBulk(
    premiseId: string,
    screenIds: string[],
    canaryPct?: number
  ): Promise<BulkActionResult> {
    return unwrap<BulkActionResult>(
      fetch(`/api/foyer/premise/${premiseId}/screens/reboot-bulk`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ screenIds, canaryPct }),
      })
    );
  },

  messageBulk(args: {
    premiseId: string;
    screenIds: string[];
    title?: string;
    body: string;
    durationMs?: number;
    canaryPct?: number;
  }): Promise<BulkActionResult> {
    const { premiseId, screenIds, ...rest } = args;
    return unwrap<BulkActionResult>(
      fetch(`/api/foyer/premise/${premiseId}/screens/message-bulk`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ screenIds, ...rest }),
      })
    );
  },

  // ── Licenses ───────────────────────────────────────────────────────
  // Read is open to any logged-in CMS user; mutations are admin-only on
  // the server. The UI hides the action buttons for non-admin users.

  /** BOUGHT vs ASSIGNED per content offering. See `licenseStore`. */
  entitlementCompliance(
    premiseId: string
  ): Promise<{ offerings: OfferingCompliance[] }> {
    return unwrap<{ offerings: OfferingCompliance[] }>(
      fetch(`/api/foyer/premise/${premiseId}/entitlement-compliance`)
    );
  },

  listLicenses(premiseId: string): Promise<LicenseRecord[]> {
    return unwrap<LicenseRecord[]>(
      fetch(`/api/foyer/premise/${premiseId}/licenses`)
    );
  },

  createLicense(input: LicenseInput): Promise<LicenseRecord> {
    return unwrap<LicenseRecord>(
      fetch(`/api/foyer/licenses`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  updateLicense(
    id: string,
    patch: Partial<Omit<LicenseInput, "premiseId">>
  ): Promise<LicenseRecord> {
    return unwrap<LicenseRecord>(
      fetch(`/api/foyer/licenses/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  deleteLicense(id: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/licenses/${id}`, { method: "DELETE" })
    );
  },

  /**
   * Add a purchase LINE to an existing licence — see {@link LicenseTierRecord}.
   */
  addLicenseTier(
    licenseId: string,
    input: {
      quantity: number | null;
      price?: LicenseMoney | null;
      notes?: string | null;
    }
  ): Promise<LicenseRecord> {
    return unwrap<LicenseRecord>(
      fetch(`/api/foyer/licenses/${licenseId}/tiers`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  removeLicenseTier(licenseId: string, tierId: string): Promise<LicenseRecord> {
    return unwrap<LicenseRecord>(
      fetch(`/api/foyer/licenses/${licenseId}/tiers/${tierId}`, {
        method: "DELETE",
      })
    );
  },

  listCompanies(): Promise<CompanySummary[]> {
    return unwrap<CompanySummary[]>(fetch(`/api/foyer/companies`));
  },

  createCompany(input: {
    name: string;
    type?: "CUSTOMER" | "RESELLER" | "SYSTEM_INTEGRATOR" | "ADVERTISER";
    parent?: string | null;
    upsalesId?: string | null;
  }): Promise<CompanySummary> {
    return unwrap<CompanySummary>(
      fetch(`/api/foyer/companies`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  async deleteCompany(id: string): Promise<{ ok: true }> {
    const res = await fetch(`/api/foyer/companies/${id}`, { method: "DELETE" });
    if (res.status === 409) {
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        premiseCount?: number;
      };
      const count = body.premiseCount ?? 0;
      throw new Error(
        `Company still has ${count} premise${count === 1 ? "" : "s"}. ` +
          `Reassign or delete those first.`
      );
    }
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return { ok: true };
  },

  deletePremise(premiseId: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/premise/${premiseId}`, { method: "DELETE" })
    );
  },

  async createPremise(input: {
    name: string;
    slug?: string;
    companyId?: string | null;
  }): Promise<PremiseConfig> {
    const res = await fetch(`/api/foyer/premises`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (res.ok) return (await res.json()) as PremiseConfig;
    if (res.status === 401) {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("foyer:unauthorized"));
      }
      throw new Error("unauthorized");
    }
    // Surface the server's structured error (e.g. slug_already_exists) so the
    // UI can show a friendly, field-level message instead of a raw status.
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      slug?: string;
    };
    throw Object.assign(
      new Error(body.error ?? `${res.status} ${res.statusText}`),
      { code: body.error, status: res.status, slug: body.slug }
    );
  },

  createUser(input: {
    email: string;
    password: string;
    type: Role;
    permissions?: string[];
    company?: string | null;
    contentOwner?: string | null;
    premises?: string[];
  }): Promise<UserRecord> {
    return unwrap<UserRecord>(
      fetch(`/api/foyer/users`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  licenseSummary(premiseId: string): Promise<LicenseSeatSummary> {
    return unwrap<LicenseSeatSummary>(
      fetch(`/api/foyer/premise/${premiseId}/licenses/summary`)
    );
  },

  /** Video-call seat pool: bought / assigned / free. Sibling of
   *  {@link licenseSummary} - the pool is a `video-call` License row, but this
   *  counts entitled RESIDENTS rather than activated screens. */
  videoCallSeatSummary(premiseId: string): Promise<VideoCallSeatSummary> {
    return unwrap<VideoCallSeatSummary>(
      fetch(`/api/foyer/premise/${premiseId}/video-call/seats/summary`)
    );
  },

  /** Who currently holds a calling seat, and through which funding channel.
   *  PHI-free (`personRef` only) and gated on `canManageCallContacts` - a 403
   *  here is the caller's answer to "may I assign seats?", not an error. */
  listVideoCallSeats(premiseId: string): Promise<VideoCallSeat[]> {
    return unwrap<VideoCallSeat[]>(
      fetch(`/api/foyer/premise/${premiseId}/video-call/seats`)
    );
  },

  /**
   * Give a resident a seat from the premise pool. Idempotent: a resident who
   * already holds one comes back with `alreadyActive: true` (and the seat that
   * funds it, which may be a family one). Rejects with `code: "pool_full"` and
   * the summary that proves it when the pool has no free seat left.
   */
  assignVideoCallSeat(
    premiseId: string,
    personRef: string
  ): Promise<{ seat: VideoCallSeat; alreadyActive: boolean }> {
    return unwrap<{ seat: VideoCallSeat; alreadyActive: boolean }>(
      fetch(`/api/foyer/premise/${premiseId}/video-call/seats`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ personRef }),
      })
    );
  },

  /** Release the resident's seat (pool seat → back to the pool; family seat →
   *  the relative's billing is stopped). Idempotent - releasing a resident who
   *  holds nothing answers `{ seat: null }`, not an error. */
  releaseVideoCallSeat(
    premiseId: string,
    personRef: string
  ): Promise<{ ok: boolean; seat: VideoCallSeat | null }> {
    return unwrap<{ ok: boolean; seat: VideoCallSeat | null }>(
      fetch(
        `/api/foyer/premise/${premiseId}/video-call/seats/${encodeURIComponent(
          personRef
        )}`,
        { method: "DELETE" }
      )
    );
  },

  listRoomTypes(): Promise<RoomTypeRecord[]> {
    return unwrap<RoomTypeRecord[]>(fetch(`/api/foyer/room-types`));
  },

  createRoomType(input: {
    name: string;
    companyId?: string | null;
    description?: string | null;
    category?: string;
  }): Promise<RoomTypeRecord> {
    return unwrap<RoomTypeRecord>(
      fetch(`/api/foyer/room-types`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  updateRoomType(
    id: string,
    patch: {
      name?: string;
      companyId?: string | null;
      description?: string | null;
      category?: string;
      /** Ordered — the array IS the order. See `RoomTypeRecord.carousels`. */
      carousels?: string[];
      pages?: string[];
      featureSets?: string[];
    }
  ): Promise<RoomTypeRecord> {
    return unwrap<RoomTypeRecord>(
      fetch(`/api/foyer/room-types/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  deleteRoomType(id: string): Promise<{ deleted: true; clearedRooms: number }> {
    return unwrap<{ deleted: true; clearedRooms: number }>(
      fetch(`/api/foyer/room-types/${id}`, { method: "DELETE" })
    );
  },

  // ── Radio station catalogue (admin-managed) ─────────────────────────

  listRadioStations(): Promise<RadioStationRecord[]> {
    return unwrap<RadioStationRecord[]>(fetch(`/api/foyer/radio-stations`));
  },

  createRadioStation(input: {
    name: string;
    tag?: string;
    logoUrl?: string | null;
    streamUrl?: string | null;
    metadataId?: string | null;
  }): Promise<RadioStationRecord> {
    return unwrap<RadioStationRecord>(
      fetch(`/api/foyer/radio-stations`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  updateRadioStation(
    id: string,
    patch: {
      name?: string;
      tag?: string;
      logoUrl?: string | null;
      streamUrl?: string | null;
      metadataId?: string | null;
    }
  ): Promise<RadioStationRecord> {
    return unwrap<RadioStationRecord>(
      fetch(`/api/foyer/radio-stations/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  deleteRadioStation(id: string): Promise<{ deleted: true }> {
    return unwrap<{ deleted: true }>(
      fetch(`/api/foyer/radio-stations/${id}`, { method: "DELETE" })
    );
  },

  // ── Global template (scheme) defaults (admin-managed) ───────────────

  listSchemeDefaults(): Promise<GlobalSchemeDefault[]> {
    return unwrap<GlobalSchemeDefault[]>(fetch(`/api/foyer/schemes/defaults`));
  },

  saveSchemeDefault(
    key: string,
    body: { name?: string | null; palette?: Record<string, string> | null }
  ): Promise<GlobalSchemeDefault> {
    return unwrap<GlobalSchemeDefault>(
      fetch(`/api/foyer/schemes/defaults/${encodeURIComponent(key)}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      })
    );
  },

  // ── App catalogue (admin-managed) ───────────────────────────────────

  listApps(): Promise<AppRecord[]> {
    return unwrap<AppRecord[]>(fetch(`/api/foyer/apps`));
  },

  createApp(input: {
    name: string;
    color?: string;
    text?: string;
    wordmark?: string;
    letter?: string;
    logoUrl?: string | null;
    tag?: string;
    androidSlug?: string | null;
    tizenId?: string | null;
    webosId?: string | null;
    iosScheme?: string | null;
    webUrl?: string | null;
    certification?: string | null;
  }): Promise<AppRecord> {
    return unwrap<AppRecord>(
      fetch(`/api/foyer/apps`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  updateApp(
    id: string,
    patch: {
      name?: string;
      color?: string;
      text?: string;
      wordmark?: string;
      letter?: string;
      logoUrl?: string | null;
      tag?: string;
      androidSlug?: string | null;
      tizenId?: string | null;
      webosId?: string | null;
      iosScheme?: string | null;
      webUrl?: string | null;
      certification?: string | null;
    }
  ): Promise<AppRecord> {
    return unwrap<AppRecord>(
      fetch(`/api/foyer/apps/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  deleteApp(id: string): Promise<{ deleted: true }> {
    return unwrap<{ deleted: true }>(
      fetch(`/api/foyer/apps/${id}`, { method: "DELETE" })
    );
  },

  // ── Hospitality / PMS ────────────────────────────────────────────────

  getPmsConfig(premiseId: string): Promise<PmsConfig> {
    return unwrap<PmsConfig>(
      fetch(`/api/foyer/premise/${premiseId}/pms-config`)
    );
  },

  putPmsConfig(premiseId: string, cfg: PmsConfig): Promise<PmsConfig> {
    return unwrap<PmsConfig>(
      fetch(`/api/foyer/premise/${premiseId}/pms-config`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(cfg),
      })
    );
  },

  listRooms(premiseId: string): Promise<Room[]> {
    return unwrap<Room[]>(fetch(`/api/foyer/premise/${premiseId}/rooms`));
  },

  /* ----- Guest web companion ----- */

  getGuestWeb(premiseId: string): Promise<{
    config: GuestWebConfig;
    baseUrl: string;
    embed: GuestEmbedUrls | null;
  }> {
    return unwrap(fetch(`/api/foyer/premise/${premiseId}/guest-web`));
  },

  putGuestWeb(
    premiseId: string,
    config: GuestWebConfig
  ): Promise<{ config: GuestWebConfig; embed: GuestEmbedUrls | null }> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/guest-web`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(config),
      })
    );
  },

  /** Mint or ROTATE the public embed key. Rotation kills the hotel's live
   *  `<img src>` the moment it returns - confirm before calling. */
  mintGuestEmbedKey(
    premiseId: string
  ): Promise<{ config: GuestWebConfig; embed: GuestEmbedUrls | null }> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/guest-web/embed-key`, {
        method: "POST",
      })
    );
  },

  deleteGuestEmbedKey(
    premiseId: string
  ): Promise<{ config: GuestWebConfig; embed: null }> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/guest-web/embed-key`, {
        method: "DELETE",
      })
    );
  },

  listGuestTokens(premiseId: string): Promise<GuestTokensResponse> {
    return unwrap(fetch(`/api/foyer/premise/${premiseId}/guest-tokens`));
  },

  guestCompanionRooms(
    premiseId: string
  ): Promise<{ enabled: boolean; roomIds: string[] }> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/guest-companion-rooms`)
    );
  },

  bulkIssueGuestTokens(
    premiseId: string,
    opts: { roomIds?: string[]; regenerate?: boolean } = {}
  ): Promise<{ issued: IssuedGuestToken[]; count: number }> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/guest-tokens/bulk`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(opts),
      })
    );
  },

  issueRoomToken(premiseId: string, roomId: string): Promise<IssuedGuestToken> {
    return unwrap(
      fetch(
        `/api/foyer/premise/${premiseId}/guest-tokens/room/${encodeURIComponent(roomId)}`,
        { method: "POST" }
      )
    );
  },

  issuePropertyToken(premiseId: string): Promise<IssuedGuestToken> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/guest-tokens/property`, {
        method: "POST",
      })
    );
  },

  revokeGuestToken(
    premiseId: string,
    tokenId: string
  ): Promise<{ revoked: true }> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/guest-tokens/${tokenId}/revoke`, {
        method: "POST",
      })
    );
  },

  revealRoomToken(
    premiseId: string,
    roomId: string
  ): Promise<{ token: string; url: string }> {
    return unwrap(
      fetch(
        `/api/foyer/premise/${premiseId}/guest-tokens/reveal/room/${encodeURIComponent(roomId)}`
      )
    );
  },

  guestAnalytics(premiseId: string, days = 30): Promise<GuestAnalyticsSummary> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/guest-analytics?days=${days}`)
    );
  },

  /** Content-engagement rollup for the in-room TVs of one premise. */
  engagement(premiseId: string, days = 30): Promise<EngagementSummary> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/engagement?days=${days}`)
    );
  },

  /** Same numbers across every property of a company (and its sub-companies). */
  chainEngagement(
    companyId: string,
    days = 30
  ): Promise<ChainEngagementSummary> {
    return unwrap(
      fetch(`/api/foyer/companies/${companyId}/engagement?days=${days}`)
    );
  },

  /**
   * CSV download URLs. Plain `href`s rather than a fetch-and-blob dance: the
   * browser already holds the auth cookie, and letting it stream the file means
   * a chain export of a year of data never has to fit in a JS string.
   */
  engagementCsvUrl(premiseId: string, days = 90): string {
    return `/api/foyer/premise/${premiseId}/engagement/export.csv?days=${days}`;
  },

  chainEngagementCsvUrl(companyId: string, days = 90): string {
    return `/api/foyer/companies/${companyId}/engagement/export.csv?days=${days}`;
  },

  /** URL for the print-ready QR PDF (GET; browser sends the auth cookie). */
  guestQrPdfUrl(premiseId: string, ensure = true): string {
    return `/api/foyer/premise/${premiseId}/guest-qr.pdf${ensure ? "?ensure=1" : ""}`;
  },

  setRoomOccupancy(
    premiseId: string,
    roomId: string,
    occupied: boolean
  ): Promise<{ ok: true; occupied: boolean }> {
    return unwrap<{ ok: true; occupied: boolean }>(
      fetch(`/api/foyer/premise/${premiseId}/room/${roomId}/occupancy`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ occupied }),
      })
    );
  },

  checkInAll(premiseId: string): Promise<{ ok: true; changed: number }> {
    return unwrap<{ ok: true; changed: number }>(
      fetch(`/api/foyer/premise/${premiseId}/check-in-all`, { method: "POST" })
    );
  },

  checkOutAll(premiseId: string): Promise<{ ok: true; changed: number }> {
    return unwrap<{ ok: true; changed: number }>(
      fetch(`/api/foyer/premise/${premiseId}/check-out-all`, { method: "POST" })
    );
  },

  // ── Care - photo mailboxes ───────────────────────────────────────────

  upsertRoomPhotoMailbox(
    premiseId: string,
    roomId: string
  ): Promise<PhotoMailboxInfo> {
    return unwrap<PhotoMailboxInfo>(
      fetch(`/api/foyer/premise/${premiseId}/rooms/${roomId}/care/mailbox`, {
        method: "POST",
      })
    );
  },

  listRoomPhotoMailboxes(premiseId: string): Promise<PhotoMailboxInfo[]> {
    return unwrap<PhotoMailboxInfo[]>(
      fetch(`/api/foyer/premise/${premiseId}/care/mailboxes`)
    );
  },

  updateRoomPhotoMailbox(
    premiseId: string,
    roomId: string,
    patch: {
      allowlist?: string[];
      active?: boolean;
      moderated?: boolean;
      retentionDays?: number;
    }
  ): Promise<PhotoMailboxInfo> {
    return unwrap<PhotoMailboxInfo>(
      fetch(`/api/foyer/premise/${premiseId}/rooms/${roomId}/care/mailbox`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  revokeRoomPhotoMailbox(
    premiseId: string,
    roomId: string
  ): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/premise/${premiseId}/rooms/${roomId}/care/mailbox`, {
        method: "DELETE",
      })
    );
  },

  listPremiseCarePhotos(
    premiseId: string,
    opts?: { roomId?: string; status?: string }
  ): Promise<
    {
      photoId: string;
      roomId: string;
      receivedAt: string;
      expiresAt: string;
      status: string;
      imageUrl: string;
      fromName: string;
      fromEmail: string;
      subject: string;
      caption: string | null;
    }[]
  > {
    const params = new URLSearchParams();
    if (opts?.roomId) params.set("roomId", opts.roomId);
    if (opts?.status) params.set("status", opts.status);
    const qs = params.toString();
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/care/photos${qs ? `?${qs}` : ""}`)
    );
  },

  /** Approve or reject a moderation-queue photo. Approving publishes
   *  photos_changed so the TV slideshow picks it up immediately. */
  setRoomCarePhotoStatus(
    premiseId: string,
    photoId: string,
    status: "approved" | "rejected"
  ): Promise<{ status: string }> {
    return unwrap<{ status: string }>(
      fetch(`/api/foyer/premise/${premiseId}/care/photos/${photoId}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ status }),
      })
    );
  },

  deleteRoomCarePhoto(
    premiseId: string,
    photoId: string
  ): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/premise/${premiseId}/care/photos/${photoId}`, {
        method: "DELETE",
      })
    );
  },

  // ── Video calling - contact directory (VC-07/VC-09) ─────────────────
  // The list view is deliberately PHI-light: endpoints come back as *masks*
  // ("+46 70 ***** 67"), never as values. Pulling a real number is its own
  // audited call (`revealCallEndpoints`) and must stay a rare, explicit act.

  listCallContacts(
    premiseId: string,
    opts: {
      scope?: CallContactScope;
      personRef?: string;
      roomTypeId?: string;
      includeRevoked?: boolean;
    } = {}
  ): Promise<CallContact[]> {
    const qs = new URLSearchParams();
    if (opts.scope) qs.set("scope", opts.scope);
    if (opts.personRef) qs.set("personRef", opts.personRef);
    if (opts.roomTypeId) qs.set("roomTypeId", opts.roomTypeId);
    if (opts.includeRevoked) qs.set("includeRevoked", "true");
    const q = qs.toString();
    return unwrap<CallContact[]>(
      fetch(`/api/foyer/premise/${premiseId}/call-contacts${q ? `?${q}` : ""}`)
    );
  },

  createCallContact(
    premiseId: string,
    input: CallContactInput
  ): Promise<CallContact> {
    return unwrap<CallContact>(
      fetch(`/api/foyer/premise/${premiseId}/call-contacts`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  /** Edit. `scope`, `roomTypeId` and `personRef` are immutable server-side. */
  updateCallContact(
    premiseId: string,
    contactId: string,
    patch: Partial<Omit<CallContactInput, "scope" | "roomTypeId" | "personRef">>
  ): Promise<CallContact> {
    return unwrap<CallContact>(
      fetch(`/api/foyer/premise/${premiseId}/call-contacts/${contactId}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  /** SOFT delete: the row survives so the call log stays explicable. */
  revokeCallContact(
    premiseId: string,
    contactId: string,
    reason?: string
  ): Promise<CallContact> {
    return unwrap<CallContact>(
      fetch(`/api/foyer/premise/${premiseId}/call-contacts/${contactId}`, {
        method: "DELETE",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ reason: reason ?? "manual" }),
      })
    );
  },

  /** Decrypt one contact's endpoints. Requires `canViewPhi`; every call writes
   *  a disclosure row - never call speculatively. */
  revealCallEndpoints(
    premiseId: string,
    contactId: string
  ): Promise<CallContactEndpoints> {
    return unwrap<CallContactEndpoints>(
      fetch(
        `/api/foyer/premise/${premiseId}/call-contacts/${contactId}/endpoints`
      )
    );
  },

  /**
   * Mint a single-use link into the relatives portal for ONE family contact
   * (PORTAL-2). The plaintext link comes back ONCE - staff hand it over
   * themselves. `emailed` says whether we also managed to mail it, which is
   * false whenever the contact has no email or no mail provider is configured;
   * it is
   * convenience, never the contract.
   */
  sendFamilyPortalInvite(
    premiseId: string,
    contactId: string
  ): Promise<{ url: string; expiresAt: string; emailed: boolean }> {
    return unwrap<{ url: string; expiresAt: string; emailed: boolean }>(
      fetch(
        `/api/foyer/premise/${premiseId}/call-contacts/${contactId}/portal-invite`,
        { method: "POST" }
      )
    );
  },

  /**
   * Mint THE call link (`/c/<token>` on call.nenda.com) for ONE family contact
   * (VC3-5) - the credential their phone trades for a session that can ANSWER
   * calls. A different thing from the portal invite above, with a different
   * lifetime: this one does not expire, it is revoked.
   *
   * Shown once. `rotated` is true when a previous link existed and has just been
   * invalidated - the operator has to be told, because a link already sent to
   * the family stopped working the instant this returned.
   */
  sendFamilyCallLink(
    premiseId: string,
    contactId: string
  ): Promise<CallLinkIssueResponse> {
    return unwrap<CallLinkIssueResponse>(
      fetch(
        `/api/foyer/premise/${premiseId}/call-contacts/${contactId}/call-link`,
        { method: "POST" }
      )
    );
  },

  /** The premise calling policy, always fully resolved (absence = off). */
  getCallPolicy(premiseId: string): Promise<ResolvedCallPolicy> {
    return unwrap<ResolvedCallPolicy>(
      fetch(`/api/foyer/premise/${premiseId}/call-config`)
    );
  },

  /**
   * The call log (VC-25) - read-only, newest first, filterable by room, date
   * and contact. Read straight from the audit trail server-side; it carries no
   * call content, ever, because the source rows never held any.
   */
  listCallLog(
    premiseId: string,
    filters: {
      roomId?: string;
      contactId?: string;
      from?: string;
      to?: string;
      limit?: number;
      beforeSeq?: number;
    } = {}
  ): Promise<CallLogPage> {
    const params = new URLSearchParams();
    if (filters.roomId) params.set("roomId", filters.roomId);
    if (filters.contactId) params.set("contactId", filters.contactId);
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    if (filters.limit !== undefined) params.set("limit", String(filters.limit));
    if (filters.beforeSeq !== undefined) {
      params.set("beforeSeq", String(filters.beforeSeq));
    }
    const qs = params.toString();
    return unwrap<CallLogPage>(
      fetch(`/api/foyer/premise/${premiseId}/call-log${qs ? `?${qs}` : ""}`)
    );
  },

  /**
   * Replace the premise calling policy (VC-09). A PUT is a FULL statement:
   * anything omitted is stored as omitted and resolves to its safe default, so
   * always send the whole block rather than a patch.
   *
   * `confirmAutoAnswer` is the caller asserting it showed the warning. The
   * server demands it on the off→on transition at a SENIOR_LIVING premise and
   * refuses with `auto_answer_confirmation_required` otherwise - which is the
   * point: a client that never rendered the dialog cannot arm a bedroom camera.
   */
  setCallPolicy(
    premiseId: string,
    policy: CallPolicyConfig & { confirmAutoAnswer?: boolean }
  ): Promise<ResolvedCallPolicy> {
    return unwrap<ResolvedCallPolicy>(
      fetch(`/api/foyer/premise/${premiseId}/call-config`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(policy),
      })
    );
  },

  // ── Care - occupancy + door signs ───────────────────────────────────
  // PHI note: the roster is PHI-free (only the policy-reduced display name).
  // `admitResident` sends a name, which the server encrypts; it is never read
  // back here.

  listOccupancy(premiseId: string): Promise<CareOccupancy[]> {
    return unwrap<CareOccupancy[]>(
      fetch(`/api/foyer/premise/${premiseId}/care/occupancy`)
    );
  },

  admitResident(
    premiseId: string,
    body: {
      roomId: string;
      person?: { name: string; dob?: string | null; mrn?: string | null };
      personRef?: string;
      externalEncounterId?: string | null;
    }
  ): Promise<CareOccupancy> {
    return unwrap<CareOccupancy>(
      fetch(`/api/foyer/premise/${premiseId}/care/occupancy/admit`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      })
    );
  },

  dischargeResident(
    premiseId: string,
    occupancyId: string
  ): Promise<CareOccupancy> {
    return unwrap<CareOccupancy>(
      fetch(
        `/api/foyer/premise/${premiseId}/care/occupancy/${occupancyId}/discharge`,
        { method: "POST" }
      )
    );
  },

  setDoorSignStatus(
    premiseId: string,
    roomId: string,
    status: DoorSignStatus
  ): Promise<{ ok: true; status: DoorSignStatus; occupancy: CareOccupancy }> {
    return unwrap(
      fetch(
        `/api/foyer/premise/${premiseId}/care/room/${roomId}/door-sign/status`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ status }),
        }
      )
    );
  },

  setDoorSignIdentityMode(
    premiseId: string,
    mode: DoorSignIdentityMode
  ): Promise<{ ok: true; mode: DoorSignIdentityMode; rebuilt: number }> {
    return unwrap(
      fetch(`/api/foyer/premise/${premiseId}/care/door-sign/identity-mode`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ mode }),
      })
    );
  },

  /** Decrypt one resident's identity. Server requires `canViewPhi` and audits
   *  every call (success AND denied) - never call speculatively. */
  revealPerson(premiseId: string, personRef: string): Promise<CarePhiReveal> {
    return unwrap<CarePhiReveal>(
      fetch(`/api/foyer/premise/${premiseId}/care/person/${personRef}`)
    );
  },

  /**
   * Åtkomstlogg for a premise - who accessed which resident's information and
   * when. Server requires `canViewPhi` and audits the read itself.
   *
   * `truncated` is part of the contract and must be surfaced to the user: a page
   * that silently drops rows is worse than one that says it is incomplete, since
   * the whole point of this view is completeness. Page with `nextBeforeSeq`.
   */
  listCareAudit(
    premiseId: string,
    opts: CareAuditFilter & { limit?: number; beforeSeq?: number } = {}
  ): Promise<CareAuditPage> {
    return unwrap<CareAuditPage>(
      fetch(`/api/foyer/premise/${premiseId}/care/audit${careAuditQuery(opts)}`)
    );
  },

  /**
   * The URL a care administrator hands to their browser to download the access
   * log as CSV. Returned as a URL rather than fetched-and-blobbed so the
   * download goes through the browser's own download machinery (progress,
   * cancel, filename from Content-Disposition) - a multi-megabyte export held in
   * a JS blob is a tab crash waiting to happen.
   */
  careAuditExportUrl(premiseId: string, opts: CareAuditFilter = {}): string {
    return `/api/foyer/premise/${premiseId}/care/audit/export.csv${careAuditQuery(opts)}`;
  },

  /** Ask the server to re-verify the premise's audit hash chain. */
  verifyCareAudit(
    premiseId: string,
    opts: { from?: string; to?: string } = {}
  ): Promise<CareAuditVerification> {
    return unwrap<CareAuditVerification>(
      fetch(
        `/api/foyer/premise/${premiseId}/care/audit/verify${careAuditQuery(opts)}`
      )
    );
  },

  /** Read a resident's clinical whiteboard profile. A resident with no profile
   *  yet reads back as an empty draft. Requires `canManageCareProfiles`. */
  getCareProfile(premiseId: string, personRef: string): Promise<CareProfile> {
    return unwrap<CareProfile>(
      fetch(`/api/foyer/premise/${premiseId}/care/person/${personRef}/profile`)
    );
  },

  /** Create/replace a resident's clinical whiteboard profile. Requires
   *  `canManageCareProfiles`; the edit is audited server-side. */
  saveCareProfile(
    premiseId: string,
    personRef: string,
    input: CareProfileInput
  ): Promise<CareProfile> {
    return unwrap<CareProfile>(
      fetch(
        `/api/foyer/premise/${premiseId}/care/person/${personRef}/profile`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(input),
        }
      )
    );
  },

  /** Staff service-request queue for a premise. Optionally filter by status.
   *  Requires `canHandleServiceRequests`. */
  listCareTasks(
    premiseId: string,
    opts: { status?: CareTaskStatus; limit?: number } = {}
  ): Promise<CareTask[]> {
    const qs = new URLSearchParams();
    if (opts.status) qs.set("status", opts.status);
    if (opts.limit) qs.set("limit", String(opts.limit));
    const suffix = qs.toString() ? `?${qs}` : "";
    return unwrap<CareTask[]>(
      fetch(`/api/foyer/premise/${premiseId}/care/tasks${suffix}`)
    );
  },

  /** Acknowledge / resolve a service request. Requires
   *  `canHandleServiceRequests`; the transition is audited server-side. */
  setCareTaskStatus(
    premiseId: string,
    taskId: string,
    status: CareTaskStatus
  ): Promise<CareTask> {
    return unwrap<CareTask>(
      fetch(`/api/foyer/premise/${premiseId}/care/tasks/${taskId}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ status }),
      })
    );
  },

  // ── Users + Invites (admin-only on the server) ──────────────────────

  /**
   * What a television in this premise would draw (`TV-3a`).
   *
   * The SAME shapers the panel calls — `carouselsForRoom` → `tvHome`,
   * `subPagesForRoom` → `tvSubPages` — under CMS auth, because a preview has
   * no pairing and therefore no screen token. The body is deliberately the
   * TV's own wire shape so the preview normalises it with `normaliseHome`,
   * exactly as it would a real read.
   */
  tvPreview(
    premiseId: string,
    opts?: { roomNumber?: string; language?: string }
  ): Promise<unknown> {
    const q = new URLSearchParams();
    if (opts?.roomNumber) q.set("roomNumber", opts.roomNumber);
    if (opts?.language) q.set("language", opts.language);
    const query = q.toString();
    return unwrap(
      fetch(
        `/api/foyer/premise/${encodeURIComponent(premiseId)}/tv-preview${
          query ? `?${query}` : ""
        }`
      )
    );
  },

  /** The content-owner organisations. Staff only; see `api/contentOwners.ts`. */
  listContentOwners(): Promise<ContentOwnerRecord[]> {
    return unwrap<ContentOwnerRecord[]>(fetch(`/api/foyer/content-owners`));
  },

  /**
   * Give an EXISTING content provider a login (`CONTENT-1`).
   *
   * `supplierId` names the `ContentSupplier` — its own id becomes the
   * login's id too, since the 2026-09-04 merge
   * (`[[mrss-feed-content-providers]]`). `slug` is derived from the name
   * when omitted, and it is the handle their own storefront is reached by —
   * so it is a decision, not a detail.
   *
   * Refuses 409 `{ error: "already_taken", field, hint }` (field `"id"` means
   * this provider already has a login) and 400 `{ error: "invalid", field }`
   * (field `"supplierId"` means no such content provider exists).
   */
  /* -- title attribution, staff (CONTENT-28) ----------------------------- */

  /** Who holds this title, if anyone. `licensed` = read-only for them. */
  getTitleOwner(assetId: string): Promise<TitleOwnerView> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/vod-titles/${encodeURIComponent(assetId)}/owner`
      )
    );
  },

  /**
   * Attribute an UNOWNED title. Refuses 409 `already_attributed` (with the
   * holder) when somebody holds it - moving is `reassignTitleOwner`.
   */
  attributeTitle(
    assetId: string,
    contentOwnerId: string
  ): Promise<TitleOwnerView> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/vod-titles/${encodeURIComponent(assetId)}/owner/claim`,
        jsonBody("POST", { contentOwnerId })
      )
    );
  },

  /** MOVE a held title to another owner. Deliberate: always sends confirm. */
  reassignTitleOwner(
    assetId: string,
    contentOwnerId: string
  ): Promise<TitleOwnerView> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/vod-titles/${encodeURIComponent(assetId)}/owner/reassign`,
        jsonBody("POST", { contentOwnerId, confirm: true })
      )
    );
  },

  /* -- owner team (CONTENT-23) ------------------------------------------ */

  /** The caller's own organisation: colleagues and open invites. */
  getOwnerTeam(): Promise<{
    members: OwnerTeamMember[];
    invites: OwnerTeamInvite[];
  }> {
    return unwrap(fetch(`/api/foyer/owner/team`));
  },

  /**
   * Invite a colleague. The organisation and the grant come from the SESSION
   * server-side - nothing sent here can change either.
   */
  inviteOwnerColleague(email: string): Promise<OwnerTeamInvite> {
    return unwrap<OwnerTeamInvite>(
      fetch(`/api/foyer/owner/team/invites`, jsonBody("POST", { email }))
    );
  },

  withdrawOwnerInvite(id: string): Promise<{ ok: true }> {
    return unwrap(
      fetch(`/api/foyer/owner/team/invites/${encodeURIComponent(id)}`, {
        method: "DELETE",
      })
    );
  },

  removeOwnerColleague(id: string): Promise<{ ok: true }> {
    return unwrap(
      fetch(`/api/foyer/owner/team/members/${encodeURIComponent(id)}`, {
        method: "DELETE",
      })
    );
  },

  createContentOwner(body: {
    supplierId: string;
    name: string;
    /** `CONTENT-23`. Required - the server refuses 400 `field: "kind"` without it. */
    kind: ContentOwnerKindValue;
    slug?: string;
    contactEmail?: string;
    enabled?: boolean;
  }): Promise<ContentOwnerRecord> {
    return unwrap<ContentOwnerRecord>(
      fetch(`/api/foyer/content-owners`, jsonBody("POST", body))
    );
  },

  /**
   * Rename, re-contact, re-rate or disable one (`CONTENT-1`/`WL-6`).
   *
   * 🚨 `brand` and `identity` are deliberately NOT here. They are the
   * supplier's own identity, editable only by them at `PATCH /owner/branding`
   * (`CONTENT-20`), and the server refuses them on this route. An operator
   * quietly correcting a studio's copyright line is a divergence the studio
   * cannot see.
   *
   * There is no delete, also deliberately: `enabled: false` ends the
   * relationship reversibly, where removing the row would orphan every asset
   * they hold.
   */
  updateContentOwner(
    id: string,
    body: {
      name?: string;
      slug?: string;
      contactEmail?: string;
      locale?: "sv" | "en";
      enabled?: boolean;
      /** `CONTENT-23`. Settable, never clearable. */
      kind?: ContentOwnerKindValue;
      /** Basis points. `null` is UNAGREED, which is not the same as 0. */
      revenueShareBps?: number | null;
    }
  ): Promise<ContentOwnerRecord> {
    return unwrap<ContentOwnerRecord>(
      fetch(
        `/api/foyer/content-owners/${encodeURIComponent(id)}`,
        jsonBody("PATCH", body)
      )
    );
  },

  /**
   * Where one of the caller's titles is being shown (`CONTENT-10`).
   *
   * `preview` is part of the answer rather than a property of the page: the
   * moment a real source is registered the label disappears on its own, instead
   * of being remembered by whoever edits this screen next.
   */
  contentReach(assetId: string): Promise<ContentReachView> {
    return unwrap(
      fetch(`/api/foyer/owner/vod-titles/${encodeURIComponent(assetId)}/reach`)
    );
  },

  /**
   * How one of the caller's titles has been rented (`CONTENT-11`).
   *
   * Institutions only — there is no personal data in this answer and the shape
   * has nowhere to put any.
   */
  contentRentals(assetId: string): Promise<ContentRentalSummary> {
    return unwrap(
      fetch(
        `/api/foyer/owner/vod-titles/${encodeURIComponent(assetId)}/rentals`
      )
    );
  },

  /** The same rows as a CSV a studio can reconcile against. */
  contentRentalsCsvUrl(assetId: string): string {
    return `/api/foyer/owner/vod-titles/${encodeURIComponent(
      assetId
    )}/rentals?format=csv`;
  },

  /**
   * Take one of the caller's titles off the shelf (`CONTENT-14`).
   *
   * Immediate and reversible. ⚠️ It stops the title being OFFERED, not
   * PLAYING — the play gate reads package membership and nothing else — which
   * is why the answer carries `stillPlayable` rather than leaving a screen to
   * imply a bigger promise.
   */
  withdrawOwnTitle(assetId: string): Promise<{
    withdrawn: boolean;
    alreadyWithdrawn?: boolean;
    stillPlayable?: boolean;
  }> {
    return unwrap(
      fetch(
        `/api/foyer/owner/vod-titles/${encodeURIComponent(assetId)}/withdraw`,
        { method: "POST" }
      )
    );
  },

  /**
   * Delete one of the caller's own titles (`CONTENT-2`).
   *
   * ⚠️ **Withdraw is a separate verb and comes first.** The route refuses a
   * title that is still live with `not_withdrawn`, and `unrestricted` — no
   * rights window at all — counts as live, which is the dangerous case rather
   * than an edge one: no window means available everywhere, forever.
   *
   * The delete is SOFT. The title goes dark immediately, a `content_deletions`
   * row records it, and the upstream purge fires after a grace period — so
   * there is a window in which it can be restored.
   */
  deleteOwnTitle(assetId: string): Promise<{ deleted: boolean }> {
    return unwrap(
      fetch(`/api/foyer/owner/vod-titles/${encodeURIComponent(assetId)}`, {
        method: "DELETE",
      })
    );
  },

  /** Put it back exactly as it was. */
  undoWithdrawOwnTitle(assetId: string): Promise<{ restored: boolean }> {
    return unwrap(
      fetch(
        `/api/foyer/owner/vod-titles/${encodeURIComponent(
          assetId
        )}/withdraw/undo`,
        { method: "POST" }
      )
    );
  },

  /**
   * The review queue (`CONTENT-17`). Staff, `canManageDistribution`.
   *
   * Built from Foyer's own `content_submissions` and enriched from upstream —
   * never the reverse. The catalogue has no submission state, no owner filter
   * and no sorting at all, so a page of it filtered to submissions would be
   * arbitrarily short with a `total` that is a lie.
   */
  listSubmissions(): Promise<SubmissionQueue> {
    return unwrap(fetch(`/api/foyer/distribution/vod-titles/submissions`));
  },

  /** Approve, reject or ask for changes. A note is mandatory on the last two. */
  reviewSubmission(
    assetId: string,
    body: {
      decision: "approve" | "reject" | "request-changes";
      note?: string;
      packages?: string[];
    }
  ): Promise<{ submission: ContentSubmission }> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/vod-titles/${encodeURIComponent(
          assetId
        )}/review`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      )
    );
  },

  /** The owner's own branding block (`CONTENT-20`). Owner-editable only. */
  ownerBranding(): Promise<OwnerBranding> {
    return unwrap(fetch(`/api/foyer/owner/branding`));
  },

  saveOwnerBranding(body: {
    brand?: ContentOwnerBrand;
    identity?: ContentOwnerIdentity;
  }): Promise<OwnerBranding> {
    return unwrap(
      fetch(`/api/foyer/owner/branding`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
    );
  },

  /* ── A supplier's own money (`WL-6`) ──────────────────────────────── */

  /**
   * Every deal this owner has with a customer of theirs.
   *
   * Owner-scoped from the session, never from the path: another owner's deal
   * is a 404 rather than a 403, because the existence of the row is itself
   * theirs to know.
   */
  ownerDeals(params?: {
    state?: DealState;
    accountId?: string;
  }): Promise<ContentDeal[]> {
    const q = new URLSearchParams();
    if (params?.state) q.set("state", params.state);
    if (params?.accountId) q.set("accountId", params.accountId);
    const query = q.toString();
    return unwrap(fetch(`/api/foyer/owner/deals${query ? `?${query}` : ""}`));
  },

  /** Invoiced and unpaid past the grace period. Default 60 days. */
  ownerOverdueDeals(graceDays?: number): Promise<ContentDeal[]> {
    const q = graceDays === undefined ? "" : `?graceDays=${graceDays}`;
    return unwrap(fetch(`/api/foyer/owner/deals/overdue${q}`));
  },

  /**
   * What they earned over a period, and what Nenda's cut of it is.
   *
   * ⚠️ Both dates absent means LAST FULL CALENDAR MONTH (UTC), not
   * everything — the server decides that, and a caller that assumed
   * "all time" would silently report one month as the lifetime figure. The
   * range is half-open `[from, to)`.
   */
  ownerStatement(range?: {
    from?: string;
    to?: string;
  }): Promise<ContentStatement> {
    const q = new URLSearchParams();
    if (range?.from) q.set("from", range.from);
    if (range?.to) q.set("to", range.to);
    const query = q.toString();
    return unwrap(
      fetch(`/api/foyer/owner/statement${query ? `?${query}` : ""}`)
    );
  },

  /** The standing terms, and what this owner still owes (`CONTENT-19`). */
  ownerAgreements(): Promise<AgreementStatus> {
    return unwrap(fetch(`/api/foyer/owner/agreements`));
  },

  /** Accept ONE document. Five behind one click records a click, not consent. */
  acceptAgreement(agreementId: string): Promise<AgreementStatus> {
    return unwrap(
      fetch(`/api/foyer/owner/agreements/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agreementId }),
      })
    );
  },

  /* ------------------------------------------------- the owner's customers */

  /**
   * A content owner's own customers (`WL-3`) — tenancy underneath a tenant.
   *
   * There is no staff twin of any of these. A supplier's customer book holds
   * natural persons, and reading it is a decision with a DPA behind it rather
   * than a route registration — see `docs/storefront-customers.md`.
   */
  storefrontAccounts(): Promise<StorefrontAccount[]> {
    return unwrap(fetch(`/api/foyer/owner/customers`));
  },

  createStorefrontAccount(body: {
    name: string;
    kind?: string;
    country?: string;
    billingRef?: string;
  }): Promise<StorefrontAccount> {
    return unwrap(
      fetch(`/api/foyer/owner/customers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
    );
  },

  updateStorefrontAccount(
    accountId: string,
    body: Partial<{
      name: string;
      kind: string;
      country: string;
      billingRef: string;
      enabled: boolean;
    }>
  ): Promise<StorefrontAccount> {
    return unwrap(
      fetch(`/api/foyer/owner/customers/${encodeURIComponent(accountId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
    );
  },

  /**
   * Everybody, or one account's people.
   *
   * `accountId: null` asks for the PRIVATE customers — a different question
   * from "everybody", which is why the parameter distinguishes absent from
   * empty rather than collapsing them.
   */
  storefrontMembers(accountId?: string | null): Promise<StorefrontMember[]> {
    const query =
      accountId === undefined
        ? ""
        : `?accountId=${encodeURIComponent(accountId ?? "")}`;
    return unwrap(fetch(`/api/foyer/owner/members${query}`));
  },

  /**
   * Invite somebody.
   *
   * ⚠️ The token comes back ONCE and is stored nowhere. It is a bearer
   * credential: whoever holds it becomes this member. Show it, send it, and
   * re-invite rather than trying to recover it.
   */
  inviteStorefrontMember(body: {
    email: string;
    accountId?: string | null;
    name?: string;
    role?: string;
  }): Promise<{ member: StorefrontMember; token: string }> {
    return unwrap(
      fetch(`/api/foyer/owner/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
    );
  },

  /** What a customer can watch right now, derived from live grants. */
  storefrontAccountAccess(accountId: string): Promise<StorefrontAccess> {
    return unwrap(
      fetch(
        `/api/foyer/owner/customers/${encodeURIComponent(accountId)}/access`
      )
    );
  },

  /** Who the supplier shell is serving, and what it paints with (`CONTENT-4`). */
  ownerShell(): Promise<OwnerShell> {
    return unwrap<OwnerShell>(fetch(`/api/foyer/owner/shell`));
  },

  /**
   * The caller's OWN titles.
   *
   * Paged from Foyer's ownership mapping, so `pagination.total` is honest and
   * a row belonging to another owner is a 404 rather than a filtered-out one.
   */
  /**
   * The caller's own titles.
   *
   * ⚠️ **Takes page and pageSize only.** The owner list REFUSES `type`,
   * `search`, `sort` and every other narrowing with `filter_not_supported_here`
   * — deliberately: that page is built from Foyer's ownership mapping, which
   * holds ids rather than metadata, so filtering it would mean paging the whole
   * catalogue and post-filtering, and the total it reported could not be
   * honest. Sending one is a 400, not an ignored parameter.
   */
  listOwnTitles(params: { page?: number; pageSize?: number } = {}): Promise<{
    titles: OwnerTitleRow[];
    pagination: { page: number; pageSize: number; total: number };
  }> {
    const q = new URLSearchParams();
    if (params.page) q.set("page", String(params.page));
    if (params.pageSize) q.set("pageSize", String(params.pageSize));
    const suffix = q.toString() ? `?${q}` : "";
    return unwrap(fetch(`/api/foyer/owner/vod-titles${suffix}`));
  },

  /**
   * The caller's own titles of one kind — for the wizard's parent pickers.
   *
   * Filtered HERE rather than on the wire, because of the refusal above. The
   * rows are whole assets (the owner list returns `decorate(asset)`, not a
   * trimmed summary), so `type` and `seriesId` are already on them; what is
   * missing upstream is the ability to ASK, not the data.
   *
   * Pages until it has everything or hits the cap, because a supplier with 60
   * titles must still see the series that is on page two. `OWNER_PAGE_MAX` is
   * 50 server-side, so this is a handful of requests for a realistic catalogue
   * and it is only ever fired while somebody is choosing a parent.
   */
  async listOwnTitlesOfType(filter: {
    type: VodAssetType;
    seriesId?: string;
  }): Promise<{ titles: OwnerTitleRow[] }> {
    const out: OwnerTitleRow[] = [];
    for (let page = 1; page <= 20; page += 1) {
      const answer = await foyerApi.listOwnTitles({ page, pageSize: 50 });
      out.push(...answer.titles);
      const seen = answer.pagination.page * answer.pagination.pageSize;
      if (!answer.titles.length || seen >= answer.pagination.total) break;
    }
    return {
      titles: out.filter(
        (row) =>
          row.type === filter.type &&
          (!filter.seriesId || row.seriesId === filter.seriesId)
      ),
    };
  },

  /**
   * Create one of the caller's OWN titles.
   *
   * The same handlers as the staff route, registered a second time under
   * `/owner` — see `registerTitleRoutes`. Bound here rather than by passing a
   * base around, so a supplier call cannot accidentally be written against a
   * staff URL: that would not fail loudly, it would 403 somewhere the shell
   * cannot explain.
   */
  createOwnTitle(body: NewAsset): Promise<VodTitleRow> {
    return unwrap<VodTitleRow>(
      fetch("/api/foyer/owner/vod-titles", jsonBody("POST", body))
    );
  },

  updateOwnTitle(
    idOrReference: string,
    body: UpdateAsset & { confirmEmptyCollections?: boolean }
  ): Promise<VodTitleRow> {
    return unwrap<VodTitleRow>(
      fetch(
        `/api/foyer/owner/vod-titles/${encodeURIComponent(idOrReference)}`,
        jsonBody("PATCH", body)
      )
    );
  },

  /**
   * One of the caller's OWN titles, in full (`ART-8`).
   *
   * `listOwnTitles` answers summaries — assetId, name, type, availability — and
   * a summary has no `images`, so anything that draws or edits artwork needs
   * this instead. Same handler as `getVodTitle`, owner-scoped, and a title that
   * is not theirs is a 404 rather than a 403.
   */
  getOwnTitle(idOrReference: string): Promise<VodTitleRow> {
    return unwrap<VodTitleRow>(
      fetch(`/api/foyer/owner/vod-titles/${encodeURIComponent(idOrReference)}`)
    );
  },

  /** Upload artwork onto one of the caller's OWN titles (`ART-2`). */
  uploadOwnTitleImage(
    idOrReference: string,
    file: File,
    meta: TitleImageMeta
  ): Promise<VodTitleRow> {
    return uploadTitleImage("owner", idOrReference, file, meta);
  },

  /** Upload artwork onto any title. Staff surface. */
  uploadVodTitleImage(
    idOrReference: string,
    file: File,
    meta: TitleImageMeta
  ): Promise<VodTitleRow> {
    return uploadTitleImage("distribution", idOrReference, file, meta);
  },

  listUsers(): Promise<UserRecord[]> {
    return unwrap<UserRecord[]>(fetch(`/api/foyer/users`));
  },

  updateUser(id: string, patch: UserPatch): Promise<UserRecord> {
    return unwrap<UserRecord>(
      fetch(`/api/foyer/users/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  deleteUser(id: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/users/${id}`, { method: "DELETE" })
    );
  },

  listInvites(): Promise<InviteRecord[]> {
    return unwrap<InviteRecord[]>(fetch(`/api/foyer/invites`));
  },

  createInvite(input: InviteInput): Promise<InviteRecord> {
    return unwrap<InviteRecord>(
      fetch(`/api/foyer/invites`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      })
    );
  },

  revokeInvite(id: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/invites/${id}`, { method: "DELETE" })
    );
  },

  // Public - no auth.
  lookupInvite(token: string): Promise<InviteLookup> {
    return unwrap<InviteLookup>(
      fetch(`/api/foyer/invites/lookup/${encodeURIComponent(token)}`)
    );
  },

  redeemInvite(token: string, password: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`/api/foyer/invites/redeem/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ password }),
      })
    );
  },

  /* -- signage -------------------------------------------------------- */

  listSignagePlaylists(premiseId: string): Promise<SignagePlaylist[]> {
    return unwrap<SignagePlaylist[]>(
      fetch(signageBase(premiseId, "playlists"))
    );
  },

  /** Playlists visible in a scope - a location's own + inherited, or a chain's. */
  listSignagePlaylistsIn(scope: SignageScopeRef): Promise<SignagePlaylist[]> {
    return unwrap<SignagePlaylist[]>(
      fetch(signageScopeBase(scope, "playlists"))
    );
  },

  /* -- screen groups ---------------------------------------------------- */

  listSignageGroupsIn(scope: SignageScopeRef): Promise<SignageScreenGroup[]> {
    return unwrap<SignageScreenGroup[]>(
      fetch(signageScopeBase(scope, "groups"))
    );
  },

  /**
   * Which member screens are ignoring each group, and what is winning instead.
   *
   * Fetched separately from the list so the table can draw first: this one has
   * to look at every rival group and every member screen, and an operator
   * opening the page wants their groups now, not after a masking check.
   */
  listSignageGroupFollowingIn(
    scope: SignageScopeRef
  ): Promise<SignageGroupFollowing[]> {
    return unwrap<SignageGroupFollowing[]>(
      fetch(`${signageScopeBase(scope, "groups")}/following`)
    );
  },

  createSignageGroupIn(
    scope: SignageScopeRef,
    body: { name: string; screenIds: string[] }
  ): Promise<SignageScreenGroup> {
    return unwrap<SignageScreenGroup>(
      fetch(signageScopeBase(scope, "groups"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
    );
  },

  updateSignageGroupIn(
    scope: SignageScopeRef,
    id: string,
    patch: { name?: string; screenIds?: string[] }
  ): Promise<SignageScreenGroup> {
    return unwrap<SignageScreenGroup>(
      fetch(`${signageScopeBase(scope, "groups")}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      })
    );
  },

  /** What a group plays, or null if it has never been given an opinion. */
  getSignageGroupAssignmentIn(
    scope: SignageScopeRef,
    id: string
  ): Promise<SignageAssignment | null> {
    return unwrap<SignageAssignment | null>(
      fetch(
        `${signageScopeBase(scope, "groups")}/${encodeURIComponent(id)}/assignment`
      )
    );
  },

  /**
   * Set what a group plays.
   *
   * `null` on a setting RETRACTS it - the group goes back to having no opinion
   * and its screens fall through to their location. Absent leaves it alone.
   */
  putSignageGroupAssignmentIn(
    scope: SignageScopeRef,
    id: string,
    patch: SignageAssignmentPatch
  ): Promise<SignageAssignment> {
    return unwrap<SignageAssignment>(
      fetch(
        `${signageScopeBase(scope, "groups")}/${encodeURIComponent(id)}/assignment`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        }
      )
    );
  },

  async deleteSignageGroupIn(
    scope: SignageScopeRef,
    id: string
  ): Promise<void> {
    const res = await fetch(
      `${signageScopeBase(scope, "groups")}/${encodeURIComponent(id)}`,
      { method: "DELETE" }
    );
    if (!res.ok) throw new Error("Could not delete that group.");
  },

  /**
   * One channel's first clip, resolved. Global scope only - it is the layer the
   * Channels catalogue browses, and the only one with a lightbox.
   */
  /**
   * `itemId` targets one row rather than the channel's first clip - see
   * `SignagePlaylistDialog`'s item preview.
   */
  signagePlaylistPreview(
    id: string,
    itemId?: string
  ): Promise<SignagePlaylistPreview> {
    const qs = itemId ? `?itemId=${encodeURIComponent(itemId)}` : "";
    return unwrap<SignagePlaylistPreview>(
      fetch(
        `/api/foyer/global/signage/playlists/${encodeURIComponent(id)}/preview${qs}`
      )
    );
  },

  /**
   * Live TV channels this scope may schedule on a wall (`BE-41`).
   *
   * Returns the envelope rather than the bare array, because `liveTvEnabled`
   * is a different state from an empty list and the caller has to be able to
   * say which: "you have not bought live TV" and "you bought it and nothing is
   * pinned yet" are two different sentences, and one blank grid for both is how
   * a customer decides the feature is broken.
   */
  listSignageChannelOptionsIn(
    scope: SignageScopeRef
  ): Promise<SignageChannelOptions> {
    return unwrap<SignageChannelOptions>(
      fetch(signageScopeBase(scope, "channel-options"))
    ).then((r) => ({
      channels: r.channels ?? [],
      // An older server that predates the field said nothing about licensing,
      // and its answer was the whole catalogue - so absent means enabled.
      liveTvEnabled: r.liveTvEnabled !== false,
    }));
  },

  /* -- signage templates ----------------------------------------------- */

  /**
   * Templates visible in a scope: this location's own, its chain's, and
   * Nenda's stock ones. Under the FILLER's gate, because filling one in
   * requires seeing what it asks for.
   */
  listSignageTemplatesIn(scope: SignageScopeRef): Promise<SignageTemplate[]> {
    return unwrap<SignageTemplate[]>(
      fetch(signageScopeBase(scope, "templates"))
    );
  },

  createSignageTemplateIn(
    scope: SignageScopeRef,
    body: Partial<SignageTemplate>
  ): Promise<SignageTemplate> {
    return unwrap<SignageTemplate>(
      fetch(signageScopeBase(scope, "templates"), jsonBody("POST", body))
    );
  },

  updateSignageTemplateIn(
    scope: SignageScopeRef,
    id: string,
    body: Partial<SignageTemplate>
  ): Promise<SignageTemplate> {
    return unwrap<SignageTemplate>(
      fetch(
        `${signageScopeBase(scope, "templates")}/${encodeURIComponent(id)}`,
        jsonBody("PATCH", body)
      )
    );
  },

  /**
   * Delete a template. REFUSES with a 409 while slides are built from it -
   * the caller has to surface the count, because "in use" is only actionable
   * when you know by how many.
   */
  deleteSignageTemplateIn(
    scope: SignageScopeRef,
    id: string
  ): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(
        `${signageScopeBase(scope, "templates")}/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
        }
      )
    );
  },

  /* -- signage slides (a filled-in template) ---------------------------- */

  listSignageSlidesIn(
    scope: SignageScopeRef
  ): Promise<SignageTemplateInstance[]> {
    return unwrap<SignageTemplateInstance[]>(
      fetch(signageScopeBase(scope, "slides"))
    );
  },

  createSignageSlideIn(
    scope: SignageScopeRef,
    body: { name: string; templateId: string; values: Record<string, string> }
  ): Promise<SignageTemplateInstance> {
    return unwrap<SignageTemplateInstance>(
      fetch(signageScopeBase(scope, "slides"), jsonBody("POST", body))
    );
  },

  updateSignageSlideIn(
    scope: SignageScopeRef,
    id: string,
    body: { name?: string; values?: Record<string, string> }
  ): Promise<SignageTemplateInstance> {
    return unwrap<SignageTemplateInstance>(
      fetch(
        `${signageScopeBase(scope, "slides")}/${encodeURIComponent(id)}`,
        jsonBody("PATCH", body)
      )
    );
  },

  deleteSignageSlideIn(
    scope: SignageScopeRef,
    id: string
  ): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`${signageScopeBase(scope, "slides")}/${encodeURIComponent(id)}`, {
        method: "DELETE",
      })
    );
  },

  /**
   * Ask whether a web page can actually be shown on a wall.
   *
   * Not scope-keyed, unlike everything around it: the question is about a
   * public URL and the answer is that URL's own HTTP headers, so there is no
   * tenant in either. See `src/server/api/signageUrlProbe.ts` for why the
   * answer is advice and never a refusal.
   */
  probeSignageUrl(url: string): Promise<SignageUrlProbe> {
    return unwrap<SignageUrlProbe>(
      fetch("/api/foyer/signage/url-check", jsonBody("POST", { url }))
    );
  },

  // Writes go to the scope ADDRESSED, never the scope a playlist was read
  // from. A chain's loop listed at one of its locations is visible there but
  // owned upstream; editing it from the location would silently rewrite what
  // every other location sees.

  createSignagePlaylistIn(
    scope: SignageScopeRef,
    body: Partial<SignagePlaylist>
  ): Promise<SignagePlaylist> {
    return unwrap<SignagePlaylist>(
      fetch(signageScopeBase(scope, "playlists"), jsonBody("POST", body))
    );
  },

  updateSignagePlaylistIn(
    scope: SignageScopeRef,
    id: string,
    body: Partial<SignagePlaylist>
  ): Promise<SignagePlaylist> {
    return unwrap<SignagePlaylist>(
      fetch(signageScopeBase(scope, `playlists/${id}`), jsonBody("PATCH", body))
    );
  },

  deleteSignagePlaylistIn(
    scope: SignageScopeRef,
    id: string
  ): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(signageScopeBase(scope, `playlists/${id}`), { method: "DELETE" })
    );
  },

  createSignageDayPlanIn(
    scope: SignageScopeRef,
    body: { name: string; blocks: SignageDayPlan["blocks"] }
  ): Promise<SignageDayPlan> {
    return unwrap<SignageDayPlan>(
      fetch(signageScopeBase(scope, "day-plans"), jsonBody("POST", body))
    );
  },

  listSignageDayPlansIn(scope: SignageScopeRef): Promise<SignageDayPlan[]> {
    return unwrap<SignageDayPlan[]>(
      fetch(signageScopeBase(scope, "day-plans"))
    );
  },

  updateSignageDayPlanIn(
    scope: SignageScopeRef,
    id: string,
    body: { name?: string; blocks?: SignageDayPlan["blocks"] }
  ): Promise<SignageDayPlan> {
    return unwrap<SignageDayPlan>(
      fetch(signageScopeBase(scope, `day-plans/${id}`), jsonBody("PATCH", body))
    );
  },

  listSignageCampaignsIn(scope: SignageScopeRef): Promise<SignageCampaign[]> {
    return unwrap<SignageCampaign[]>(
      fetch(signageScopeBase(scope, "campaigns"))
    );
  },

  createSignageCampaignIn(
    scope: SignageScopeRef,
    body: Partial<SignageCampaign>
  ): Promise<SignageCampaign> {
    return unwrap<SignageCampaign>(
      fetch(signageScopeBase(scope, "campaigns"), jsonBody("POST", body))
    );
  },

  /**
   * The venue-content library an effect channel is curated from.
   *
   * Nenda's scope only. What a customer sees is the finished channel, never
   * its parts - the same stance `channel-options` takes.
   */
  listEffectLibrary(opts: { page?: number; q?: string } = {}): Promise<{
    items: Array<{
      assetId: string;
      title: string;
      image: string | null;
      playable: boolean;
    }>;
    total: number;
    page: number;
    pageSize: number;
    unconfigured: boolean;
  }> {
    const qs = new URLSearchParams();
    if (opts.page) qs.set("page", String(opts.page));
    if (opts.q?.trim()) qs.set("q", opts.q.trim());
    const suffix = qs.toString() ? `?${qs}` : "";
    return unwrap(fetch(`/api/foyer/global/signage/effect-library${suffix}`));
  },

  /**
   * Real titles + posters for a channel's OWN catalogue items - what
   * `SignagePlaylistDialog`'s item rows show instead of a generic mark. Capped
   * server-side (`HYDRATE_SUMMARIES_LIMIT`); a channel with more items than
   * that just keeps the generic mark on the rest.
   */
  hydrateSignageContentAssets(assetIds: string[]): Promise<{
    items: Array<{
      assetId: string;
      title: string;
      image: string | null;
      playable: boolean;
    }>;
  }> {
    return unwrap(
      fetch(
        "/api/foyer/global/signage/content-assets/hydrate",
        jsonBody("POST", { assetIds })
      )
    );
  },

  updateSignageCampaignIn(
    scope: SignageScopeRef,
    id: string,
    body: Partial<SignageCampaign>
  ): Promise<SignageCampaign> {
    return unwrap<SignageCampaign>(
      fetch(signageScopeBase(scope, `campaigns/${id}`), jsonBody("PATCH", body))
    );
  },

  deleteSignageCampaignIn(
    scope: SignageScopeRef,
    id: string
  ): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(signageScopeBase(scope, `campaigns/${id}`), { method: "DELETE" })
    );
  },

  deleteSignageDayPlanIn(
    scope: SignageScopeRef,
    id: string
  ): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(signageScopeBase(scope, `day-plans/${id}`), { method: "DELETE" })
    );
  },

  /** One screen's own assignment row (not the resolved, layered view). */
  getSignageAssignment(
    premiseId: string,
    screenId: string
  ): Promise<SignageAssignment> {
    return unwrap<SignageAssignment>(
      fetch(signageBase(premiseId, `screens/${screenId}`))
    );
  },

  createSignagePlaylist(
    premiseId: string,
    body: Partial<SignagePlaylist>
  ): Promise<SignagePlaylist> {
    return unwrap<SignagePlaylist>(
      fetch(signageBase(premiseId, "playlists"), jsonBody("POST", body))
    );
  },

  updateSignagePlaylist(
    premiseId: string,
    id: string,
    body: Partial<SignagePlaylist>
  ): Promise<SignagePlaylist> {
    return unwrap<SignagePlaylist>(
      fetch(signageBase(premiseId, `playlists/${id}`), jsonBody("PATCH", body))
    );
  },

  deleteSignagePlaylist(premiseId: string, id: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(signageBase(premiseId, `playlists/${id}`), { method: "DELETE" })
    );
  },

  listSignageCampaigns(premiseId: string): Promise<SignageCampaign[]> {
    return unwrap<SignageCampaign[]>(
      fetch(signageBase(premiseId, "campaigns"))
    );
  },

  createSignageCampaign(
    premiseId: string,
    body: Partial<SignageCampaign>
  ): Promise<SignageCampaign> {
    return unwrap<SignageCampaign>(
      fetch(signageBase(premiseId, "campaigns"), jsonBody("POST", body))
    );
  },

  updateSignageCampaign(
    premiseId: string,
    id: string,
    body: Partial<SignageCampaign>
  ): Promise<SignageCampaign> {
    return unwrap<SignageCampaign>(
      fetch(signageBase(premiseId, `campaigns/${id}`), jsonBody("PATCH", body))
    );
  },

  deleteSignageCampaign(premiseId: string, id: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(signageBase(premiseId, `campaigns/${id}`), { method: "DELETE" })
    );
  },

  listSignageDayPlans(premiseId: string): Promise<SignageDayPlan[]> {
    return unwrap<SignageDayPlan[]>(fetch(signageBase(premiseId, "day-plans")));
  },

  createSignageDayPlan(
    premiseId: string,
    body: Partial<SignageDayPlan>
  ): Promise<SignageDayPlan> {
    return unwrap<SignageDayPlan>(
      fetch(signageBase(premiseId, "day-plans"), jsonBody("POST", body))
    );
  },

  updateSignageDayPlan(
    premiseId: string,
    id: string,
    body: Partial<SignageDayPlan>
  ): Promise<SignageDayPlan> {
    return unwrap<SignageDayPlan>(
      fetch(signageBase(premiseId, `day-plans/${id}`), jsonBody("PATCH", body))
    );
  },

  deleteSignageDayPlan(premiseId: string, id: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(signageBase(premiseId, `day-plans/${id}`), { method: "DELETE" })
    );
  },

  listSignageAssignments(premiseId: string): Promise<SignageAssignment[]> {
    return unwrap<SignageAssignment[]>(
      fetch(signageBase(premiseId, "screens"))
    );
  },

  putSignageAssignment(
    premiseId: string,
    screenId: string,
    body: Partial<SignageAssignment>
  ): Promise<SignageAssignment> {
    return unwrap<SignageAssignment>(
      fetch(
        signageBase(premiseId, `screens/${screenId}`),
        jsonBody("PUT", body)
      )
    );
  },

  /**
   * The resolved playout for one screen.
   *
   * `at` scrubs the window without waiting for the clock, so an operator can
   * check "what does this show on Sunday morning?" today.
   */
  getSignageTimeline(
    premiseId: string,
    screenId: string,
    opts: { at?: Date; hours?: number } = {}
  ): Promise<SignageTimeline> {
    const q = new URLSearchParams();
    if (opts.at) q.set("at", opts.at.toISOString());
    if (opts.hours) q.set("hours", String(opts.hours));
    const suffix = q.toString() ? `?${q}` : "";
    return unwrap<SignageTimeline>(
      fetch(signageBase(premiseId, `screens/${screenId}/timeline${suffix}`))
    );
  },

  // ── Distribution: the Content API facade (BE-15) ─────────────────────
  //
  // Every one of these goes page → here → Foyer's routes → the `contentApi`
  // port → nenda-content-api. The browser never talks to the catalogue: that
  // service has ONE static key granting full control of the whole catalogue
  // with no tenancy of any kind, so it can only ever live on a server.
  //
  // Bodies are the WIRE shapes (`NewAsset` / `UpdateAsset`), built by
  // `@shared/asset-draft`. A paraphrase in this file would be the third copy of
  // a model that already exists twice, and paraphrasing is what made BE-1…BE-6
  // lossy.

  listDistributionChannels(
    query: AssetListQuery = {}
  ): Promise<ChannelListPage> {
    return unwrap<ChannelListPage>(
      fetch(`/api/foyer/distribution/channels${assetListQuery(query)}`)
    );
  },

  /**
   * Nenda's official channel line-up — the order every premise inherits.
   *
   * An ORDER, not a selection: naming a channel here grants nothing and puts it
   * on no rail. See `@shared/channel-order`.
   */
  channelOrder(): Promise<{ channelOrder: string[] }> {
    return unwrap<{ channelOrder: string[] }>(
      fetch("/api/foyer/distribution/channel-order")
    );
  },

  /** Replaces the official order whole — the list IS the order. */
  saveChannelOrder(
    channelOrder: string[]
  ): Promise<{ channelOrder: string[] }> {
    return unwrap<{ channelOrder: string[] }>(
      fetch(
        "/api/foyer/distribution/channel-order",
        jsonBody("PUT", { channelOrder })
      )
    );
  },

  getDistributionChannel(assetId: string): Promise<ChannelRow> {
    return unwrap<ChannelRow>(
      fetch(`/api/foyer/distribution/channels/${encodeURIComponent(assetId)}`)
    );
  },

  createDistributionChannel(body: NewAsset): Promise<ChannelRow> {
    return unwrap<ChannelRow>(
      fetch("/api/foyer/distribution/channels", jsonBody("POST", body))
    );
  },

  updateDistributionChannel(
    assetId: string,
    body: UpdateAsset & { confirmEmptyCollections?: boolean }
  ): Promise<ChannelRow> {
    return unwrap<ChannelRow>(
      fetch(
        `/api/foyer/distribution/channels/${encodeURIComponent(assetId)}`,
        jsonBody("PATCH", body)
      )
    );
  },

  /** Refused with `channel_in_use` while any premise carries it. */
  deleteDistributionChannel(assetId: string): Promise<{ deleted: true }> {
    return unwrap<{ deleted: true }>(
      fetch(`/api/foyer/distribution/channels/${encodeURIComponent(assetId)}`, {
        method: "DELETE",
      })
    );
  },

  // ── Carousels and sub-pages: the DISPLAY layer (BE-28) ───────────────
  //
  // These are FOYER'S OWN rows, not the Content API's — which is why they are
  // a different block from the facade above. `Package` grants ACCESS,
  // `Carousel` grants DISPLAY, and merging them is the obvious mistake.
  //
  // The catalogue is gated by `canManageDistribution`. The SELECTION (which
  // rails a room type shows, in what order) is NOT: it lives on `/room-types`
  // under `canManageRoomTypes`, which customers already hold.

  listCarousels(): Promise<{ carousels: CarouselRow[]; total: number }> {
    return unwrap<{ carousels: CarouselRow[]; total: number }>(
      fetch("/api/foyer/distribution/carousels")
    );
  },

  getCarousel(id: string): Promise<CarouselRow> {
    return unwrap<CarouselRow>(
      fetch(`/api/foyer/distribution/carousels/${encodeURIComponent(id)}`)
    );
  },

  createCarousel(body: CarouselBody): Promise<CarouselRow> {
    return unwrap<CarouselRow>(
      fetch("/api/foyer/distribution/carousels", jsonBody("POST", body))
    );
  },

  updateCarousel(id: string, body: CarouselBody): Promise<CarouselRow> {
    return unwrap<CarouselRow>(
      fetch(
        `/api/foyer/distribution/carousels/${encodeURIComponent(id)}`,
        jsonBody("PATCH", body)
      )
    );
  },

  /** Refused with `carousel_in_use` while any room type or page shows it. */
  deleteCarousel(id: string): Promise<{ deleted: true }> {
    return unwrap<{ deleted: true }>(
      fetch(`/api/foyer/distribution/carousels/${encodeURIComponent(id)}`, {
        method: "DELETE",
      })
    );
  },

  carouselUsage(id: string): Promise<{ holders: CarouselHolder[] }> {
    return unwrap<{ holders: CarouselHolder[] }>(
      fetch(`/api/foyer/distribution/carousels/${encodeURIComponent(id)}/usage`)
    );
  },

  /**
   * What id a name would take, and whether it is already taken.
   *
   * The create dialog asks BEFORE the operator has typed a whole carousel into
   * it, because upstream's own create silently OVERWRITES on a collision.
   */
  carouselSlugPreview(name: string): Promise<{ id: string; taken: boolean }> {
    return unwrap<{ id: string; taken: boolean }>(
      fetch(
        `/api/foyer/distribution/carousel-slug?name=${encodeURIComponent(name)}`
      )
    );
  },

  /* ── Content packages and suppliers (BE-30) ─────────────────────────────
   *
   * A package is a RULE over trusted suppliers, plus assets for the exceptions.
   * `docs/content-packages.md` is the whole argument; the two things to carry
   * into any UI over this:
   *
   *  - **trust is a separate verb.** Every route here needs
   *    `canManageDistribution`; `trustSupplier` needs `canTrustSuppliers` on
   *    top, because flipping it publishes a supplier's future titles to guests
   *    unattended.
   *  - **`resolved.truncated` means the count is a prefix.** Any screen that
   *    draws a number must say so; see `docs/content-packages.md` §4.
   */

  listContentSuppliers(): Promise<{
    suppliers: ContentSupplierRow[];
    catalogueReachable: boolean;
  }> {
    return unwrap<{
      suppliers: ContentSupplierRow[];
      catalogueReachable: boolean;
    }>(fetch("/api/foyer/distribution/suppliers"));
  },

  /**
   * Mint a real provider — never available before this, anywhere. The legacy
   * portal's own "add provider" tab writes a tag, not a row a rule can carry.
   *
   * `providerId` is optional: left blank, one is derived from the name. Either
   * way it comes back `foyer-<slug>` — minted, never trusted verbatim.
   */
  createContentSupplier(body: {
    name: string;
    providerId?: string;
  }): Promise<{ supplier: ContentSupplierRow }> {
    return unwrap(
      fetch("/api/foyer/distribution/suppliers", jsonBody("POST", body))
    );
  },

  /**
   * Forget a content provider entirely. Refused (409) if it is currently
   * trusted, still delivers for a content-owner login, or holds real titles
   * in the catalogue — untrust / unbind / neither, in that order, is what
   * the hint on each refusal says to do first.
   */
  deleteContentSupplier(supplierId: string): Promise<{ deleted: true }> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/suppliers/${encodeURIComponent(supplierId)}`,
        { method: "DELETE" }
      )
    );
  },

  /** Give every upstream provider a Foyer row. Never changes trust. */
  syncContentSuppliers(): Promise<{ synced: number; created: number }> {
    return unwrap<{ synced: number; created: number }>(
      fetch("/api/foyer/distribution/suppliers/sync", { method: "POST" })
    );
  },

  trustSupplier(
    supplierId: string,
    trusted: boolean
  ): Promise<{
    supplier: ContentSupplierRow;
    affectedPackages: { id: string; name: string }[];
  }> {
    return unwrap<{
      supplier: ContentSupplierRow;
      affectedPackages: { id: string; name: string }[];
    }>(
      fetch(
        `/api/foyer/distribution/suppliers/${encodeURIComponent(supplierId)}/trust`,
        jsonBody("PUT", { trusted })
      )
    );
  },

  /**
   * Attach, change or clear a provider's MRSS feed — Type C in the A/B/C
   * shape (`[[mrss-feed-content-providers]]`). `null` clears it (and the
   * channel mapping with it).
   *
   * Deliberately independent of a login: a feed-only aggregator that nobody
   * at the source ever logs into needs exactly this and nothing else.
   *
   * `effectChannelId` travels in the SAME call, not a follow-up one: the
   * server refuses a non-null `mrssFeedUrl` with no channel chosen (a real
   * production incident - a feed saved with no channel read its whole
   * catalogue as "new" forever). Omit it entirely (leave undefined) to edit
   * just the URL and keep whatever mapping is already there.
   */
  setSupplierMrssFeed(
    supplierId: string,
    mrssFeedUrl: string | null,
    effectChannelId?: string | null
  ): Promise<{
    supplier: Pick<
      ContentSupplierRow,
      "id" | "name" | "mrssFeedUrl" | "effectChannelId"
    >;
  }> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/suppliers/${encodeURIComponent(supplierId)}/feed`,
        jsonBody("PUT", { mrssFeedUrl, effectChannelId })
      )
    );
  },

  /**
   * Which global effect channel an item approved from this supplier's feed
   * joins automatically. `null` clears it - approved items then exist only
   * as VOD titles, entitled through media collections instead.
   */
  setSupplierEffectChannel(
    supplierId: string,
    effectChannelId: string | null
  ): Promise<{
    supplier: Pick<ContentSupplierRow, "id" | "name" | "effectChannelId">;
  }> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/suppliers/${encodeURIComponent(supplierId)}/effect-channel`,
        jsonBody("PUT", { effectChannelId })
      )
    );
  },

  /**
   * Fetch + parse a provider's feed and diff it against what has already been
   * ingested through this flow. Read-only and repeatable - see
   * `feedScanStore`.
   */
  scanSupplierFeed(supplierId: string): Promise<{ items: FeedScanItem[] }> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/suppliers/${encodeURIComponent(supplierId)}/feed/scan`
      )
    );
  },

  /**
   * Turn one feed item into a real VOD title in one request: mints the
   * asset, best-effort its poster, registers the ingest job, and starts
   * copying its master in.
   */
  approveFeedItem(
    supplierId: string,
    guid: string,
    body: {
      title?: string;
      synopsisLong?: string;
      synopsisMedium?: string;
      synopsisShort?: string;
      packages: string[];
    }
  ): Promise<{ assetId: string; title: string; jobId: string }> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/suppliers/${encodeURIComponent(supplierId)}/feed/items/${encodeURIComponent(guid)}/approve`,
        jsonBody("POST", body)
      )
    );
  },

  /** The offerings — what a customer is SOLD (`ENT-1`). */
  /** Create a product. Packages are granted separately, via
   *  {@link setFeatureSetPackages}, which is the one place unknown ids are
   *  refused. */
  createFeatureSet(body: {
    id: string;
    name?: string;
    description?: string;
    /** What `License.quantity` counts. Omitted = "room". `null` = no amount. */
    seatUnit?: SeatUnit;
  }): Promise<{
    product: {
      id: string;
      name: string;
      packages: string[];
      seatUnit: SeatUnit;
    };
  }> {
    return unwrap(
      fetch("/api/foyer/distribution/feature-sets", jsonBody("POST", body))
    );
  },

  /**
   * Withdraw a product from a premise, taking its dependants with it.
   *
   * Cancels, never deletes: the licence carries the AGREED PRICE, and that row
   * is the only place a held rate exists. Use {@link deleteLicense} only for a
   * row typed by mistake.
   */
  withdrawLicense(id: string): Promise<{ cancelled: string[] }> {
    return unwrap<{ cancelled: string[] }>(
      fetch(`/api/foyer/licenses/${encodeURIComponent(id)}/withdraw`, {
        method: "POST",
      })
    );
  },

  /**
   * What every room in a premise may play, compiled from its licences.
   * Same function the write path uses, so it cannot disagree with the rooms.
   */
  premiseEntitlement(premiseId: string): Promise<{
    /** The PALETTE — what this premise licensed, and how many rooms hold it. */
    featureSets: { id: string; name: string; rooms: number }[];
    /** What each room is assigned. The intent, not the compiled effect. */
    rooms: { roomNumber: string; products: string[] }[];
  }> {
    return unwrap(
      fetch(`/api/foyer/premise/${encodeURIComponent(premiseId)}/entitlement`)
    );
  },

  /**
   * Assign products to rooms. Takes the COMPLETE list per room — a partial one
   * silently revokes the rest — and is refused for anything unlicensed.
   */
  assignRoomProducts(
    premiseId: string,
    assignments: { roomNumber: string; products: string[] }[]
  ): Promise<{ ok: true; rooms: number }> {
    return unwrap(
      fetch(
        `/api/foyer/premise/${encodeURIComponent(premiseId)}/room-products`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ assignments }),
        }
      )
    );
  },

  /**
   * What a dynamic collection's filter matches in the CATALOGUE.
   *
   * 🚨 Not what any room sees. The builder is global and has no premise, so
   * there is no entitlement to filter by — this answers "what could this rail
   * ever contain", and the caller must say so.
   */
  previewCarousel(draft: {
    featureSetId?: string;
    tagIds?: string[];
    assetTypeFilter?: string[];
    sourceType?: string;
  }): Promise<CarouselPreviewMatch> {
    return unwrap<CarouselPreviewMatch>(
      fetch(
        "/api/foyer/distribution/carousels/preview",
        jsonBody("POST", draft)
      )
    );
  },

  /** One premise's home page: what it arranged, and what its licences add. */
  premiseRails(premiseId: string): Promise<PremiseRails> {
    return unwrap<PremiseRails>(
      fetch(`/api/foyer/premise/${encodeURIComponent(premiseId)}/rails`)
    );
  },

  /** States only what changed. A stated `[]` empties; absent leaves alone. */
  /**
   * The channels this premise can show, in the order it will show them.
   *
   * `usingOfficial` means it has not departed from the national line-up — an
   * empty override, not an empty list.
   */

  premiseChannelOrder(premiseId: string): Promise<PremiseChannelOrder> {
    return unwrap<PremiseChannelOrder>(
      fetch(`/api/foyer/premise/${encodeURIComponent(premiseId)}/channel-order`)
    );
  },

  /** Whole-list write. An empty list goes back to the national line-up. */
  savePremiseChannelOrder(
    premiseId: string,
    channelOrder: string[]
  ): Promise<{ premiseId: string; channelOrder: string[] }> {
    return unwrap<{ premiseId: string; channelOrder: string[] }>(
      fetch(
        `/api/foyer/premise/${encodeURIComponent(premiseId)}/channel-order`,
        jsonBody("PUT", { channelOrder })
      )
    );
  },

  savePremiseRails(
    premiseId: string,
    body: {
      carousels?: string[];
      subPages?: string[];
      disabledCarouselIds?: string[];
    }
  ): Promise<PremiseRails> {
    return unwrap<PremiseRails>(
      fetch(
        `/api/foyer/premise/${encodeURIComponent(premiseId)}/rails`,
        jsonBody("PUT", body)
      )
    );
  },

  listFeatureSets(): Promise<{ featureSets: FeatureSetRow[] }> {
    return unwrap<{ featureSets: FeatureSetRow[] }>(
      fetch("/api/foyer/distribution/feature-sets")
    );
  },

  /**
   * Set which packages an offering grants.
   *
   * Takes the COMPLETE list, never a delta — the server refuses a partial one,
   * because "add package X" that arrives as the whole field silently revokes
   * everything else.
   */
  setFeatureSetPackages(
    featureSetId: string,
    packages: string[],
    /** Omitted leaves it as it was; `packages` has no such option on purpose. */
    seatUnit?: SeatUnit
  ): Promise<{ ok: true; packages: string[] }> {
    return unwrap<{ ok: true; packages: string[] }>(
      fetch(
        `/api/foyer/distribution/feature-sets/${encodeURIComponent(featureSetId)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            seatUnit === undefined ? { packages } : { packages, seatUnit }
          ),
        }
      )
    );
  },

  listContentPackages(): Promise<{ packages: ContentPackageRow[] }> {
    return unwrap<{ packages: ContentPackageRow[] }>(
      fetch("/api/foyer/distribution/packages")
    );
  },

  getContentPackage(
    id: string
  ): Promise<{ package: ContentPackageRow; diff: ContentPackageDiff }> {
    return unwrap<{ package: ContentPackageRow; diff: ContentPackageDiff }>(
      fetch(`/api/foyer/distribution/packages/${encodeURIComponent(id)}`)
    );
  },

  createContentPackage(body: {
    id: string;
    name?: string;
    description?: string;
  }): Promise<{ package: ContentPackageRow }> {
    return unwrap<{ package: ContentPackageRow }>(
      fetch("/api/foyer/distribution/packages", jsonBody("POST", body))
    );
  },

  updateContentPackage(
    id: string,
    body: {
      name?: string;
      description?: string;
      rule?: PackageRule | null;
      assets?: string[];
      excludes?: string[];
    }
  ): Promise<{
    package: ContentPackageRow;
    /**
     * Present when the edit changed what the package HOLDS. `null` for a
     * rename — resolving every curated package to say a description overlaps
     * nothing is a fan-out for no information.
     */
    overlap: PackageOverlapReport | null;
  }> {
    return unwrap<{
      package: ContentPackageRow;
      overlap: PackageOverlapReport | null;
    }>(
      fetch(
        `/api/foyer/distribution/packages/${encodeURIComponent(id)}`,
        jsonBody("PATCH", body)
      )
    );
  },

  /**
   * What a candidate rule WOULD do, without saving it.
   *
   * The migration off the enumerated lists turns on this: nobody should be
   * asked to trade a list they can see for a rule they cannot.
   */
  previewPackageRule(
    id: string,
    rule: PackageRule | null
  ): Promise<{
    preview: ContentPackagePreview;
    problems: { field: string; message: string }[];
    normalised: PackageRule | null;
    /** What this CANDIDATE rule would make the package a near-copy of. */
    overlap: PackageOverlapReport;
  }> {
    return unwrap<{
      preview: ContentPackagePreview;
      problems: { field: string; message: string }[];
      normalised: PackageRule | null;
      overlap: PackageOverlapReport;
    }>(
      fetch(
        `/api/foyer/distribution/packages/${encodeURIComponent(id)}/preview`,
        jsonBody("POST", { rule })
      )
    );
  },

  /**
   * 🚨 These three shipped on the SERVER at BE-28 and were never called.
   *
   * `POST/PATCH/DELETE /distribution/sub-pages` have existed, tested, since
   * 2026-08-06, and `foyerApi` carried only the read — so a sub-page could not
   * be created, renamed or published from the portal at all, and
   * `SubPage.draft` (which defaults to TRUE and is the only publish gate
   * anywhere in this model) was unflippable. Pages existed and were invisible.
   */
  createSubPage(body: Partial<SubPage>): Promise<SubPageRow> {
    return unwrap<SubPageRow>(
      fetch("/api/foyer/distribution/sub-pages", jsonBody("POST", body))
    );
  },

  /** States only what changed. A stated `[]` empties; absent leaves alone. */
  updateSubPage(id: string, body: Partial<SubPage>): Promise<SubPageRow> {
    return unwrap<SubPageRow>(
      fetch(
        `/api/foyer/distribution/sub-pages/${encodeURIComponent(id)}`,
        jsonBody("PATCH", body)
      )
    );
  },

  /** 409 `sub_page_in_use` names the room types still pointing at it. */
  deleteSubPage(id: string): Promise<{ deleted: boolean }> {
    return unwrap<{ deleted: boolean }>(
      fetch(`/api/foyer/distribution/sub-pages/${encodeURIComponent(id)}`, {
        method: "DELETE",
      })
    );
  },

  listSubPages(): Promise<{ subPages: SubPageRow[]; total: number }> {
    return unwrap<{ subPages: SubPageRow[]; total: number }>(
      fetch("/api/foyer/distribution/sub-pages")
    );
  },

  listVodTitles(query: VodListQuery = {}): Promise<VodListPage> {
    const params = new URLSearchParams(assetListParams(query));
    for (const type of query.type ?? []) params.append("type", type);
    if (query.sourceType) params.set("sourceType", query.sourceType);
    const suffix = params.toString() ? `?${params}` : "";
    return unwrap<VodListPage>(
      fetch(`/api/foyer/distribution/vod-titles${suffix}`)
    );
  },

  /**
   * One total per type, each read as a `pagination.total`.
   *
   * Not derived from a list page: counting a page is the exact lie this whole
   * model was corrected to stop making.
   */
  vodTitleCounts(): Promise<Record<VodAssetType, number>> {
    return unwrap<Record<VodAssetType, number>>(
      fetch("/api/foyer/distribution/vod-titles/counts")
    );
  },

  /**
   * Every unplayable title and every channel missing metadata - the Overview
   * page's content-health list. The server sweeps the whole catalogue and holds
   * the answer; `refresh` asks it to look again.
   */
  catalogueHealth(
    query: {
      problem?: CatalogueProblemCode;
      page?: number;
      pageSize?: number;
      refresh?: boolean;
    } = {}
  ): Promise<CatalogueHealthPage> {
    const params = new URLSearchParams();
    if (query.problem) params.set("problem", query.problem);
    if (query.page) params.set("page", String(query.page));
    if (query.pageSize) params.set("pageSize", String(query.pageSize));
    if (query.refresh) params.set("refresh", "1");
    const suffix = params.toString() ? `?${params}` : "";
    return unwrap<CatalogueHealthPage>(
      fetch(`/api/foyer/distribution/health${suffix}`)
    );
  },

  /** Takes an `assetId` OR a `NENDA-…` reference; the server resolves both. */
  getVodTitle(idOrReference: string): Promise<VodTitleRow> {
    return unwrap<VodTitleRow>(
      fetch(
        `/api/foyer/distribution/vod-titles/${encodeURIComponent(idOrReference)}`
      )
    );
  },

  /**
   * What "Download" would take - the top rung of the HLS ladder and its audio
   * tracks - or, as a 409/502 carrying `hint`/`detail`, why nothing can be.
   * Asked first because the file itself cannot explain a failure.
   */
  vodDownloadPlan(idOrReference: string): Promise<VodDownloadPlan> {
    return unwrap<VodDownloadPlan>(
      fetch(
        `/api/foyer/distribution/vod-titles/${encodeURIComponent(idOrReference)}/download/plan`
      )
    );
  },

  /**
   * The file itself. A URL, not a fetch: it is handed to the browser's own
   * download manager so a whole film streams to disk, not into a tab.
   */
  vodDownloadUrl(idOrReference: string): string {
    return `/api/foyer/distribution/vod-titles/${encodeURIComponent(idOrReference)}/download`;
  },

  getVodSeries(idOrReference: string): Promise<VodSeriesTree> {
    return unwrap<VodSeriesTree>(
      fetch(
        `/api/foyer/distribution/vod-titles/${encodeURIComponent(
          idOrReference
        )}/series`
      )
    );
  },

  createVodTitle(body: NewAsset): Promise<VodTitleRow> {
    return unwrap<VodTitleRow>(
      fetch("/api/foyer/distribution/vod-titles", jsonBody("POST", body))
    );
  },

  updateVodTitle(
    idOrReference: string,
    body: UpdateAsset & { confirmEmptyCollections?: boolean }
  ): Promise<VodTitleRow> {
    return unwrap<VodTitleRow>(
      fetch(
        `/api/foyer/distribution/vod-titles/${encodeURIComponent(
          idOrReference
        )}`,
        jsonBody("PATCH", body)
      )
    );
  },

  deleteVodTitle(idOrReference: string): Promise<{ deleted: true }> {
    return unwrap<{ deleted: true }>(
      fetch(
        `/api/foyer/distribution/vod-titles/${encodeURIComponent(
          idOrReference
        )}`,
        { method: "DELETE" }
      )
    );
  },

  /**
   * Give an imported title a `NENDA-…` number.
   *
   * The counterpart to the server never minting one on a read: a catalogue
   * dominated by machine-generated catch-up rows would otherwise be numbered
   * end to end by nothing but being looked at. Idempotent - the same asset
   * always answers with the same number.
   */
  mintVodReference(
    idOrReference: string
  ): Promise<{ assetId: string; reference: string }> {
    return unwrap<{ assetId: string; reference: string }>(
      fetch(
        `/api/foyer/distribution/vod-titles/${encodeURIComponent(
          idOrReference
        )}/reference`,
        { method: "POST" }
      )
    );
  },

  /**
   * One channel's schedule, over a window the caller states explicitly (`BE-27`).
   *
   * Both instants are required and both are sent as UTC `Z` strings, because
   * the route's window rule is the port's and the port refuses an open-ended
   * read - the upstream endpoint is unpaginated, so `from` with no `to` returns
   * every event forward forever.
   *
   * ⚠️ There is no "on now" in the answer and there must never be one: the read
   * behind it is cached in ten-minute bins, so a baked `live` flag is wrong
   * within seconds. Call `eventAt(events, Date.now())` from `@shared/epg-guide`
   * against the browser's own clock instead.
   */
  getChannelSchedule(
    assetId: string,
    window: { from: string; to: string; lang?: string }
  ): Promise<ChannelSchedule> {
    const params = new URLSearchParams({ from: window.from, to: window.to });
    if (window.lang) params.set("lang", window.lang);
    return unwrap<ChannelSchedule>(
      fetch(
        `/api/foyer/distribution/channels/${encodeURIComponent(
          assetId
        )}/epg?${params}`
      )
    );
  },

  /**
   * One event's artwork and metadata - what a schedule row deliberately does
   * not carry (see `@shared/epg-guide`'s header on `toGuideEvent`), fetched
   * for exactly the one event an operator clicked.
   */
  getEpgEventDetail(
    channelAssetId: string,
    epgEventId: number,
    lang?: string
  ): Promise<{ event: GuideEventDetail }> {
    const params = lang ? `?${new URLSearchParams({ lang })}` : "";
    return unwrap<{ event: GuideEventDetail }>(
      fetch(
        `/api/foyer/distribution/channels/${encodeURIComponent(
          channelAssetId
        )}/epg/${encodeURIComponent(String(epgEventId))}${params}`
      )
    );
  },

  /**
   * Set a channel's EPG source id (`BE-22`).
   *
   * PUT and there is no delete, because upstream has no removal path: this
   * writes once and the server refuses a change. The failure codes matter more
   * than usual — `mapping_taken` carries the asset already holding the id, and
   * naming it is the only way an operator can act on a collision that surfaces
   * upstream as a bare 500.
   */
  setChannelEpgMapping(
    assetId: string,
    alternativeId: string
  ): Promise<{ channel: Asset; mapping: string; changed: boolean }> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/channels/${encodeURIComponent(assetId)}/epg-mapping`,
        jsonBody("PUT", { alternativeId })
      )
    );
  },

  listContentProviders(): Promise<Provider[]> {
    return unwrap<{ providers: Provider[] }>(
      fetch("/api/foyer/distribution/providers")
    ).then((r) => r.providers);
  },

  listContentGenres(): Promise<Genre[]> {
    return unwrap<{ genres: Genre[] }>(
      fetch("/api/foyer/distribution/genres")
    ).then((r) => r.genres);
  },

  /**
   * The same list, on the owner surface. Bound separately for the reason
   * `ingestCalls` exists: a supplier call written against a staff URL 403s
   * where the shell cannot explain it.
   */
  listOwnGenres(): Promise<Genre[]> {
    return unwrap<{ genres: Genre[] }>(fetch("/api/foyer/owner/genres")).then(
      (r) => r.genres
    );
  },

  /* ── Ingest (BE-17 / CONTENT-9): see `ingestCalls` above ─────────── */

  listIngestEntitlements(): Promise<{
    collections: Array<{ id: string; name: string }>;
  }> {
    return unwrap(fetch("/api/foyer/distribution/entitlements"));
  },

  /**
   * Which packages carry this title, and why (`BE-32`).
   *
   * The reachability fact: a title in no package is invisible to every guest,
   * however green everything else is. `reason` distinguishes a pin from a rule
   * from a deliberate carve-out.
   */
  listAssetPackages(assetId: string): Promise<{
    packages: Array<{
      id: string;
      name: string;
      member: boolean;
      reason: "pinned" | "rule" | "excluded" | "none";
    }>;
    totalPackages: number;
  }> {
    return unwrap(
      fetch(
        `/api/foyer/distribution/assets/${encodeURIComponent(assetId)}/packages`
      )
    );
  },

  /* ── Room offers, the authoring catalogue (POS-2 server, POS-5 CMS) ──── */

  /**
   * Everything authored, not everything live.
   *
   * The list deliberately answers with the disabled rows and the out-of-season
   * ones too — a row IS its draft, and a catalogue that hid what it could not
   * currently show would give an operator no way to reach the spa evening they
   * are preparing for December. The television reads a different URL entirely
   * (`/offers`, POS-3's), so the two can never be confused for each other.
   *
   * At the premise layer the answer INHERITS: this hotel's own rows, plus its
   * chain's, plus Nenda's global starter catalogue, each tagged with a derived
   * `scope`. Only the ones matching the addressed layer may be written back.
   */
  listRoomOffers(scope: OfferScopeRef): Promise<RoomOffer[]> {
    return unwrap<RoomOffer[]>(fetch(offerCatalogueBase(scope)));
  },

  /** Create on exactly one layer. The server defaults `enabled` to false. */
  createRoomOffer(
    scope: OfferScopeRef,
    body: RoomOfferBody
  ): Promise<RoomOffer> {
    return unwrap<RoomOffer>(
      fetch(offerCatalogueBase(scope), jsonBody("POST", body))
    );
  },

  /**
   * State only what changed.
   *
   * A PATCH that mentions the title is not judged for an action it never
   * touched, and an absent `items` leaves the pick list alone while a stated
   * `[]` empties it. Sending the whole row every time is the destructive
   * -replace bug with extra steps.
   */
  updateRoomOffer(
    scope: OfferScopeRef,
    id: string,
    body: RoomOfferBody
  ): Promise<RoomOffer> {
    return unwrap<RoomOffer>(
      fetch(
        `${offerCatalogueBase(scope)}/${encodeURIComponent(id)}`,
        jsonBody("PATCH", body)
      )
    );
  },

  deleteRoomOffer(scope: OfferScopeRef, id: string): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`${offerCatalogueBase(scope)}/${encodeURIComponent(id)}`, {
        method: "DELETE",
      })
    );
  },

  /* ── POS-14: one hotel's decisions about the rows it inherits ────────── */

  /**
   * The deltas this property holds — never copies of what it inherited.
   *
   * Fetched beside the catalogue and combined by `resolveRoomOffers()` in
   * `@shared/roomOfferLayers`, which is the SAME function the television's feed
   * runs. Two resolvers that agree by luck stop agreeing the first time
   * somebody edits one, and the disagreement shows up as a price in the CMS that
   * is not the price on the screen.
   */
  listOfferOverrides(premiseId: string): Promise<RoomOfferOverride[]> {
    return unwrap<RoomOfferOverride[]>(fetch(offerOverrideBase(premiseId)));
  },

  /**
   * Re-decide, or switch off, one INHERITED row.
   *
   * A PUT keyed on the offer's own id, because there is at most one answer per
   * property per row — the upsert is what keeps that true in the database as
   * well as in the model. `patch` is replaced whole; an absent key means
   * "inherit it", a `null` means "clear it here", and the two are different.
   */
  putOfferOverride(
    premiseId: string,
    offerId: string,
    body: { suppressed?: boolean; patch?: OfferOverridePatch }
  ): Promise<RoomOfferOverride> {
    return unwrap<RoomOfferOverride>(
      fetch(
        `${offerOverrideBase(premiseId)}/${encodeURIComponent(offerId)}`,
        jsonBody("PUT", body)
      )
    );
  },

  /* ── Guest requests, the staff inbox (POS-6 server, POS-9 CMS) ────────── */

  /**
   * The queue a hotel works its requests off. Premise-scoped, newest first,
   * and windowed to `QUEUE_WINDOW_DAYS` server-side.
   *
   * A CHAIN view is this call once per hotel rather than a company-layer route:
   * a request belongs to a room, a room belongs to one location, and there is
   * no such thing as a company's own request. Grouping by hotel is a rendering
   * decision, so it is made in the CMS and not in the data.
   *
   * Gated by `canHandleGuestRequests` on the server too — the nav gate is the
   * courtesy, this is the rule.
   */
  listGuestRequests(
    premiseId: string,
    opts: { status?: GuestRequestStatus; limit?: number } = {}
  ): Promise<GuestRequest[]> {
    const q = new URLSearchParams();
    if (opts.status) q.set("status", opts.status);
    if (opts.limit) q.set("limit", String(opts.limit));
    const query = q.toString();
    return unwrap<GuestRequest[]>(
      fetch(
        `/api/foyer/premise/${encodeURIComponent(premiseId)}/offers/requests${
          query ? `?${query}` : ""
        }`
      )
    );
  },

  /** Stop overriding: the property goes back to exactly what it inherits. */
  deleteOfferOverride(
    premiseId: string,
    offerId: string
  ): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      fetch(`${offerOverrideBase(premiseId)}/${encodeURIComponent(offerId)}`, {
        method: "DELETE",
      })
    );
  },

  /**
   * How far a chain-scope save actually reaches.
   *
   * Queried rather than counted from the CMS's own directory, because the
   * directory is a cache and the banner is a promise. "This affects multiple
   * locations" is the sentence somebody clicks past.
   */
  offerReach(companyId: string): Promise<OfferReach> {
    return unwrap<OfferReach>(
      fetch(`/api/foyer/company/${encodeURIComponent(companyId)}/offers/reach`)
    );
  },

  /**
   * Move one request along the machine. The verb is the body, exactly as the
   * route takes it — five verbs, one call, and `@shared/guestRequests` is the
   * only thing that decides which of them a row may be offered.
   *
   * `reason` is the sentence a decline owes the guest: optional, stamped beside
   * the actor, and rendered on the television by POS-7.
   */
  transitionGuestRequest(
    premiseId: string,
    id: string,
    action: GuestRequestTransition,
    reason?: string
  ): Promise<GuestRequest> {
    return unwrap<GuestRequest>(
      fetch(
        `/api/foyer/premise/${encodeURIComponent(
          premiseId
        )}/offers/requests/${encodeURIComponent(id)}/transition`,
        jsonBody("POST", reason ? { action, reason } : { action })
      )
    );
  },

  /**
   * Which payment provider this deployment is bound to (`POS-8`).
   *
   * Deliberately NOT premise-scoped: the provider is a property of the process,
   * chosen once at boot. What the CMS does with the answer is draw
   * `DemoPaymentsBanner` — see that component, which is the only thing that
   * should be calling this.
   */
  getOfferPaymentStatus(): Promise<OfferPaymentStatus> {
    return unwrap<OfferPaymentStatus>(
      fetch("/api/foyer/offers/payment-status")
    );
  },

  /**
   * What the offers on one hotel's televisions actually earned (`POS-11`).
   *
   * Impressions, engaged views and opens on the left; requests, acceptances and
   * revenue on the right; joined only in the report and never per guest. The
   * CMS renders it and re-cuts it, but never re-derives a total —
   * `@shared/offerFunnel` already summed these, and a second implementation
   * that agrees by luck stops agreeing the first time one of them is edited.
   */
  offerFunnel(premiseId: string, days = 30): Promise<OfferFunnelSummary> {
    return unwrap<OfferFunnelSummary>(
      fetch(
        `/api/foyer/premise/${encodeURIComponent(
          premiseId
        )}/offers/funnel?days=${days}`
      )
    );
  },

  /** The same, for every property of a company the session may see. */
  chainOfferFunnel(
    companyId: string,
    days = 30
  ): Promise<ChainOfferFunnelSummary> {
    return unwrap<ChainOfferFunnelSummary>(
      fetch(
        `/api/foyer/companies/${encodeURIComponent(
          companyId
        )}/offers/funnel?days=${days}`
      )
    );
  },

  /**
   * The download URLs, as plain links rather than fetches: the browser streams
   * the file and carries the session cookie itself, so a year of a chain's
   * revenue never has to be materialised in a JS string first.
   */
  offerFunnelCsvUrl(premiseId: string, days = 30): string {
    return `/api/foyer/premise/${encodeURIComponent(
      premiseId
    )}/offers/funnel/export.csv?days=${days}`;
  },

  chainOfferFunnelCsvUrl(companyId: string, days = 30): string {
    return `/api/foyer/companies/${encodeURIComponent(
      companyId
    )}/offers/funnel/export.csv?days=${days}`;
  },
};

/* ── Bulk ingest wire shapes (BE-31) ─────────────────────────────────── */

export interface BatchItemView {
  key: string;
  videoPath: string;
  title: string;
  blocked: boolean;
  findings: PackageFinding[];
  entitlements: string[];
  assetId: string | null;
  jobId: string | null;
  error: string | null;
  phase: IngestPhase | null;
  progress: number | null;
  jobError: string | null;
}

export interface BatchView {
  id: string;
  name: string;
  intent: BatchIntent;
  createdBy: string;
  createdAt: string;
  startedAt: string | null;
  scannedAt: string | null;
  layout: PackageLayout | null;
  entitlements: string[];
  packageFindings: PackageFinding[];
  rollup: BatchRollup;
  items: BatchItemView[];
}

/* ── Carousels and sub-pages (BE-28) ─────────────────────────────────── */

/**
 * A carousel row.
 *
 * **A row IS its draft.** It carries every field the editor edits, plus the two
 * things only a read can know (`languages`, `issues`) — both DERIVED on the
 * server by the same functions the editor calls over the draft in hand, so a
 * row and its dialog cannot word the same finding two ways.
 */
/* ── Content packages (BE-30) ─────────────────────────────────────────────── */

/**
 * A supplier Foyer knows about — and whether we trust them.
 *
 * `known: false` means the provider exists in the catalogue but Foyer has no
 * row for it, i.e. **nobody has ever ruled on it**. That is the most useful
 * sentence the suppliers screen can say, which is why an unknown provider is
 * listed rather than filtered out.
 */
export interface ContentSupplierRow {
  id: string;
  name: string;
  trusted: boolean;
  trustedAt: string | null;
  trustedBy: string | null;
  note: string | null;
  known: boolean;
  /**
   * Does this provider have a login? Since the 2026-09-04 merge
   * (`[[mrss-feed-content-providers]]`) a login's id IS this row's own id —
   * there is no separate binding left to represent, only whether one exists.
   * `false` is not cosmetic: a provider with no login cannot receive an
   * upload at all, because there is no account to attribute it to.
   */
  hasLogin: boolean;
  /**
   * An MRSS feed this provider also delivers through — Type C in the A/B/C
   * shape (`[[mrss-feed-content-providers]]`). `null` is the ordinary case.
   *
   * Deliberately independent of `hasLogin`: a feed-only aggregator that
   * nobody at the source ever logs into can carry one without a login
   * existing at all. Scanned via `scanSupplierFeed` on the review queue's
   * Feed view.
   */
  mrssFeedUrl: string | null;
  /**
   * The global effect channel an item approved from this supplier's feed
   * joins automatically - no picker on every approve, one mapping set here.
   * `null` means approved items exist only as VOD titles, entitled through
   * whatever media collections the reviewer picks instead.
   */
  effectChannelId: string | null;
}

/**
 * One item out of a supplier's MRSS feed, tagged against what Foyer has
 * already ingested through the Feed review flow. The item shape is the
 * server parser's own (`@shared/mrss-feed-asset`), not a hand-kept copy.
 */
export type FeedScanItem = MrssFeedItem & {
  /** Already ingested through this flow. */
  in: boolean;
};

/**
 * An offering, as the CMS lists it — what a customer is SOLD.
 *
 * `packages[].missing` is resolved server-side: a grant pointing at a package
 * Foyer does not hold grants nothing, silently, and the list is the only place
 * anybody would notice.
 */
/** What a licence's `quantity` counts. `null` = the product has no amount. */
export type SeatUnit = "screen" | "room" | "resident" | null;

/**
 * A premise's rail set, every row accounted for.
 *
 * `source` is the field that matters: a home page is now partly computed, and a
 * computed row an operator cannot account for is how a system starts feeling
 * haunted. A `product` row names the product that put it there.
 */
/** What a query carousel's filter matches, before any room narrows it. */
export interface CarouselPreviewMatch {
  /** `product` resolved through a product's packages; `catalogue` is a filter. */
  scope: "product" | "catalogue";
  assets: Asset[];
  /** Everything the filter matches — NOT the length of `assets`. */
  totalCount: number;
  /** A rule-based package hit its enumeration cap, so this is a prefix. */
  truncated: boolean;
}

export interface PremiseChannelOrder {
  premiseId: string;
  /** This hotel still follows the national line-up — an empty override. */
  usingOfficial: boolean;
  channels: {
    id: string;
    name: string;
    /** `own` = this hotel moved it. `unranked` = on neither list yet. */
    source: "own" | "official" | "unranked";
  }[];
}

export interface PremiseRails {
  premiseId: string;
  /**
   * The app's tabs, in order. No `source` here — nothing derives a page: a
   * licence can imply a rail, but a page is a container somebody composed.
   */
  pages: {
    id: string;
    name: string;
    /** Default TRUE, and the only publish gate anywhere in this model. */
    draft: boolean;
    /**
     * The rails ON this page, in order. Named rather than counted, because the
     * menu is a TREE and "2 rows" cannot be dragged or reasoned about.
     */
    carousels: {
      id: string;
      name: string;
      type: string | null;
      missing: boolean;
    }[];
    missing: boolean;
  }[];
  rails: {
    id: string;
    name: string;
    type: string | null;
    source: "chosen" | "product";
    productId: string | null;
    productName: string | null;
    disabled: boolean;
    /** The id resolves to no carousel. Drawn as a warning, never dropped. */
    missing: boolean;
    /**
     * This rail also being a MENU ROW, and what it opens. `null` = not one.
     *
     * 🚨 Read-only for a premise admin. Both halves live on the global
     * `Carousel` (`page.enabled`, `page.carousels`), so ticking a rail into the
     * menu lands on every customer holding it — that is Nenda's decision. A
     * premise decides the ORDER, which the menu inherits because the menu IS
     * the rail set filtered to `page.enabled`.
     */
    menu: {
      /** Menu-only: the row is not on the home screen at all. */
      hiddenFromHome: boolean;
      opens: { id: string; name: string; missing: boolean }[];
    } | null;
  }[];
}

export interface FeatureSetRow {
  id: string;
  name: string;
  description: string | null;
  businessUnit: string | null;
  /** The Upsales / RedBee cross-reference. Carried, not yet acted on. */
  externalRef: string | null;
  enabled: boolean | null;
  type: string | null;
  price: { amount?: number; currency?: string } | null;
  packages: { id: string; name: string; missing: boolean }[];
  /** A Nenda capability this product unlocks, if any. */
  capability: string | null;
  /**
   * What `quantity` counts — and whether an Amount is asked for at all.
   * `null` means the product HAS no amount (Signage is on or off), which is
   * NOT `quantity: null`, which means unlimited.
   */
  seatUnit: SeatUnit;
  /**
   * Products that must already be licensed. EFFECTIVE, not authored: a content
   * product's dependency on Streaming TV is derived server-side, so a screen
   * that gated on the raw field would offer exactly what the API refuses.
   */
  requires: string[];
}

export interface ContentPackageRow {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean | null;
  /** Explicit includes. The portal's `assets[]`, under the name it now means. */
  assets: string[];
  excludes: string[];
  rule: PackageRule | null;
  resolved: {
    ruleCount: number;
    /** The count is a PREFIX. Never draw it as a total without saying so. */
    truncated: boolean;
    /** No rule, or no trusted supplier in it — so it matches nothing today. */
    inert: boolean;
  } | null;
  updatedAt: string | null;
}

export interface ContentPackageDiff {
  ruleOnly: string[];
  pinsOnly: string[];
  covered: string[];
}

export interface ContentPackagePreview extends ContentPackageDiff {
  ruleCount: number;
  truncated: boolean;
  total: number | null;
  inert: boolean;
}

/**
 * One existing package this one is a near-copy of (`ENT-3`, v2 §8).
 *
 * `share` is `|A∩B| / |A|` — the share of THIS package the other already
 * carries, asymmetric on purpose. A strict subset scores 1.0, because
 * everything it grants is already granted.
 */
export interface PackageOverlap {
  id: string;
  name: string;
  share: number;
  shared: number;
  /** The other package's resolved size, so `share` reads honestly. */
  theirs: number;
  /** Ids this package has and the other does not — the carve-out. */
  onlyHere: number;
  onlyThere: number;
  onlyHereSample: string[];
  onlyThereSample: string[];
}

export interface PackageOverlapReport {
  /** This package's resolved size. `0` ⇒ nothing to compare. */
  members: number;
  /** At or above the 80% threshold, closest first. */
  overlaps: PackageOverlap[];
  /** An enumeration hit its cap, so the shares are a floor. */
  truncated: boolean;
  /** More curated packages exist than were compared. */
  candidatesTruncated: boolean;
}

export interface CarouselRow extends Carousel {
  /** Derived from `title[].language`. The list's `Språk` column. */
  languages: string[];
  /** Reported, never refusing. `carouselFindingText` is the only wording. */
  issues: CarouselIssue[];
}

export type SubPageRow = SubPage;

/**
 * What a create or update may state.
 *
 * A PATCH states ONLY what changed — an absent collection is left alone, a
 * stated `[]` empties it. That distinction is the whole reason this is a
 * `Partial` rather than the whole draft: sending all of it every time is how
 * the destructive-replace class of bug works.
 */
export type CarouselBody = Partial<
  Omit<Carousel, "id" | "createdAt" | "updatedAt">
>;

/** What is still showing a carousel, as the 409 body names it. */
export interface CarouselHolder {
  kind: "room-type" | "sub-page";
  id: string;
  name?: string;
}

/* ── Distribution list queries and rows ──────────────────────────────── */

/**
 * What both Distribution lists can ask for.
 *
 * `sort`/`order` are deliberately narrow at the call sites: the catalogue has
 * NO sorting, so only two orders are free - newest-first, and relevance while a
 * search is running. Anything else makes the server page the complete filtered
 * set and it refuses past 2 000 rows rather than sorting a prefix and lying
 * about the rest.
 */
export interface AssetListQuery {
  page?: number;
  pageSize?: number;
  sort?: AssetSort;
  order?: SortOrder;
  /** Substring over `originalTitle`. Three characters minimum upstream. */
  search?: string;
  /** FULLTEXT over one language's `LocalizedInfo.title`; needs `titleLang`. */
  title?: string;
  titleLang?: string;
  providerId?: string[];
  genreId?: string[];
  keyword?: string[];
  availableInCountry?: string;
  seriesId?: string;
  seasonId?: string;
}

export interface VodListQuery extends AssetListQuery {
  type?: VodAssetType[];
  /** `vod` or `deeplink`; `live` and `catchup` are refused as authored types. */
  sourceType?: "vod" | "deeplink";
}

function assetListParams(query: AssetListQuery): URLSearchParams {
  const params = new URLSearchParams();
  const single: [string, string | number | undefined][] = [
    ["page", query.page],
    ["pageSize", query.pageSize],
    ["sort", query.sort],
    ["order", query.order],
    ["search", query.search],
    ["title", query.title],
    ["titleLang", query.titleLang],
    ["availableInCountry", query.availableInCountry],
    ["seriesId", query.seriesId],
    ["seasonId", query.seasonId],
  ];
  for (const [key, value] of single) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  // Repeatable upstream, and an OR within the parameter. `append`, never a
  // comma-joined string: a comma is a legitimate character in a keyword.
  for (const [key, values] of [
    ["providerId", query.providerId],
    ["genreId", query.genreId],
    ["keyword", query.keyword],
  ] as const) {
    for (const value of values ?? []) params.append(key, value);
  }
  return params;
}

function assetListQuery(query: AssetListQuery): string {
  const params = assetListParams(query);
  return params.toString() ? `?${params}` : "";
}

/**
 * A channel as the list route answers it: the wire asset, plus what Foyer
 * derives about it on every read and stores nowhere.
 */
export interface ChannelRow extends Asset {
  availability: Availability;
  rightsIssues: RightsIssue[];
  imageIssues: ImageIssue[];
  /** Why this row matched, present only when something was searched. */
  matches?: AssetMatch[];
}

/**
 * A VOD title as the list route answers it.
 *
 * `reference` is Foyer's own `assetId → NENDA-…` mapping, null for a title
 * nobody has asked to number. `blockers` says whether it is fit to put in front
 * of a guest; `issues` is everything worth telling an operator that is not a
 * blocker.
 */
export interface VodTitleRow extends Asset {
  reference: string | null;
  blockers: AssetPlaybackBlocker[];
  availability: Availability;
  matches?: AssetMatch[];
  issues: {
    years: YearIssue[];
    duration: DurationIssue[];
    genreDelivery: { kind: string; [key: string]: unknown }[];
    rights: RightsIssue[];
    localised: LocalisedIssue[];
    images: ImageIssue[];
  };
}

/** `GET /distribution/vod-titles/:id/download/plan` - see `api/vodTitleDownload.ts`. */
export interface VodDownloadPlan {
  assetId: string;
  fileName: string;
  manifestUrl: string;
  /** An encrypted rendition existed and was passed over for the clear one. */
  skippedEncrypted: boolean;
  video: {
    uri: string;
    width: number | null;
    height: number | null;
    bandwidth: number | null;
    codecs: string | null;
  };
  /** Separate audio playlists muxed in. Empty when audio rides in the video. */
  audio: { name: string | null; language: string | null }[];
  muxedAudio: boolean;
  /** False when this API host cannot assemble a download at all (no ffmpeg). */
  available: boolean;
  busy: boolean;
}

export interface ChannelListPage {
  channels: ChannelRow[];
  pagination: PaginationMetadata;
  sort: AppliedSort;
}

/**
 * One EPG read, as `GET /distribution/channels/:id/epg` answers it (`BE-20`).
 *
 * The `Guide` half is the shared model - resolved events, the overlap losers,
 * the unplaceable rows and the holes - and is identical on both sides. What the
 * route adds is what the CLIENT cannot know: how old the cached read is, and
 * the bounds it was answered under.
 */
export interface ChannelSchedule extends Guide {
  /**
   * `binSeconds` is the width of the cache bin; `ageSeconds` how long the
   * events have been held. Worth showing: a guide that looks stale is then
   * explainable rather than suspicious.
   */
  cache: { binSeconds: number; ageSeconds: number };
  limits: { maxChannels: number; maxWindowDays: number };
}

export interface VodListPage {
  titles: VodTitleRow[];
  pagination: PaginationMetadata;
  sort: AppliedSort;
}

/** A series assembled from its sibling rows - there is no `episodes[]` upstream. */
export interface VodSeriesTree {
  series: VodTitleRow;
  seasons: {
    seasonNumber: number | null;
    season: Asset | null;
    episodes: Asset[];
  }[];
  looseEpisodes: Asset[];
  counts: { seasons: number; episodes: number; complete: boolean };
  blockers: SeriesPlaybackBlocker[];
  unplayableEpisodes: {
    assetId: string;
    seasonNumber: number | null;
    blockers: AssetPlaybackBlocker[];
  }[];
}

export interface UserRecord {
  id: string;
  email: string;
  type: Role;
  permissions: string[];
  company: string | null;
  /** The content owner this login speaks for (`CONTENT-3`), or null. */
  contentOwner: string | null;
  premises: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UserPatch {
  type?: Role;
  permissions?: string[];
  company?: string | null;
  contentOwner?: string | null;
  premises?: string[];
  password?: string;
}

export interface InviteRecord {
  id: string;
  email: string;
  type: Role;
  permissions: string[];
  company: string | null;
  contentOwner: string | null;
  premises: string[];
  invitedBy: string;
  acceptUrl: string;
  expiresAt: string;
  redeemedAt: string | null;
  createdAt: string;
  status: "pending" | "expired" | "redeemed";
}

export interface InviteInput {
  email: string;
  type: Role;
  permissions?: string[];
  company?: string | null;
  contentOwner?: string | null;
  premises?: string[];
}

/**
 * One case in the review queue (`CONTENT-17`).
 *
 * ⚠️ **The row carries everything the drawer decides on.** Approve acts on what
 * it was handed, so a half-loaded row is a decision made about a title nobody
 * saw — the `cms-list-and-editor` rule.
 */
export interface SubmissionRow extends ContentSubmission {
  ownerName: string;
  title: string;
  type: string | null;
  reference: string | null;
  languages: string[];
  genres: string[];
  /** Can a reviewer watch what a guest would watch? */
  playable: boolean;
  playableBlockers: unknown[];
  /**
   * Why the master is not being worked on, or null if nothing is holding it.
   *
   * ⚠️ `awaiting_review` means the reviewer reading this row IS the blocker —
   * the dispatcher skips held jobs, so nothing is encoding and nothing will
   * until they approve it. Saying "still landing" to that is a promise the
   * page itself is breaking.
   */
  holdReason: "awaiting_review" | "incomplete" | null;
}

export interface SubmissionQueue {
  submissions: SubmissionRow[];
  /** The packages an approval may pin into. Sent WITH the queue. */
  packages: Array<{ id: string; name: string }>;
  truncated: boolean;
  total: number;
}

/** Where a title is placed, and whether that answer is real yet (`CONTENT-10`). */
export interface ContentReachView {
  entries: Array<{
    kind: "collection" | "effect-channel";
    id: string;
    label: string;
    premiseCount?: number;
  }>;
  /** True while any contributing source is a stand-in. Must be shown. */
  preview: boolean;
  total: number;
}

/** A supplier's branding block, with what their colours actually resolve to. */
export interface OwnerBranding {
  brand: ContentOwnerBrand;
  identity: ContentOwnerIdentity;
  theme: SupplierShellTheme;
}

/** What the supplier shell draws itself from (`CONTENT-4`). */
export interface OwnerShell {
  owner: { id: string; name: string; slug: string };
  theme: SupplierShellTheme;
}

/** One row of a content owner's own catalogue. */
export interface OwnerTitleRow {
  assetId: string;
  originalTitle: string;
  type: string;
  reference: string | null;
  /**
   * `CONTENT-28`: a title Nenda has licensed, attributed to this studio by the
   * backfill. Read-only on the owner surface - the server refuses every write
   * with `licensed_title_read_only`, and the page hides its controls.
   */
  licensed?: boolean;
  availability?: { state: string };
  /**
   * The parent links. Present because the owner list answers whole assets
   * rather than a summary — the wizard filters on them client-side, since the
   * route refuses to filter on the wire (`filter_not_supported_here`).
   */
  seriesId?: string;
  seasonId?: string;
  seasonNumber?: number | null;
  episodeNumber?: number | null;
}

/** A content-owner organisation as the CMS lists it (`CONTENT-1`). */
export interface ContentOwnerRecord {
  /** The `ContentSupplier` this login belongs to — never a bare Mongo id. */
  id: string;
  name: string;
  slug: string;
  contactEmail: string;
  enabled: boolean;
  /** `CONTENT-23`. `null` only on owners created before it existed. */
  kind?: ContentOwnerKindValue | null;
  /** How many catalogue rows they hold. */
  titles: number;
  /**
   * The owner's share, in basis points (`WL-6`).
   *
   * `null` means UNAGREED and is not the same as `0`. Staff-settable and
   * owner-invisible: a supplier editing their own share is a supplier writing
   * their own invoice.
   */
  revenueShareBps: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * What kind of organisation a content owner is (`CONTENT-23`).
 *
 * A local mirror of `CONTENT_OWNER_KINDS` in `@shared/content-owner`, not an
 * import: a runtime import of that module is not safe in the standalone CMS
 * build that actually ships (`global/pitfalls/shared-alias-build-lanes.md`).
 * `contentOwnerKinds.test.ts` pins the two lists together.
 */
export type ContentOwnerKindValue =
  | "provider"
  | "creator"
  | "influencer"
  | "branded"
  | "aggregator";

export const CONTENT_OWNER_KIND_OPTIONS: Array<{
  value: ContentOwnerKindValue;
  label: string;
}> = [
  { value: "provider", label: "Premium content provider" },
  { value: "aggregator", label: "Content aggregator" },
  { value: "branded", label: "Branded content owner" },
  { value: "creator", label: "UGC creator" },
  { value: "influencer", label: "Influencer" },
];

/** Who holds a title (`CONTENT-28`). All null when nobody does. */
export interface TitleOwnerView {
  contentOwnerId: string | null;
  name: string | null;
  /** Attributed by Nenda rather than uploaded: read-only for the owner. */
  licensed: boolean;
}

/** One colleague in the caller's own content-owner organisation (`CONTENT-23`). */
export interface OwnerTeamMember {
  id: string;
  email: string;
  isSelf: boolean;
  /** Appointed by Nenda staff; not removable from the team page. */
  isTeamAdmin: boolean;
  createdAt: string;
}

export interface OwnerTeamInvite {
  id: string;
  email: string;
  invitedBy: string;
  expiresAt: string;
  /** Only on the answer to the invite that created it. */
  acceptUrl?: string;
}

export interface InviteLookup {
  email: string;
  invitedBy: string;
  expiresAt: string;
}

export interface RoomTypeRecord {
  id: string;
  /**
   * The customer this type belongs to, or null for the shared catalogue
   * (BE-28). Two companies may hold a type of the same name; one shared
   * catalogue may not hold two.
   */
  companyId: string | null;
  name: string;
  description: string | null;
  /** `room` | `public` | `common_area` — upstream's `RoomTypeCategory`. */
  category: string;
  /**
   * The SELECTION layer: ordered `Carousel._id` values. **Array position IS the
   * display order.** Authored by the customer under `canManageRoomTypes`, not
   * by Nenda under `canManageDistribution`.
   */
  carousels: string[];
  /** Ordered `SubPage._id` values. */
  pages: string[];
  /** `FeatureSet._id` values every room of this type is granted (BE-14). */
  featureSets: string[];
  roomCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RadioStationRecord {
  id: string;
  name: string;
  tag: string;
  logoUrl: string | null;
  streamUrl: string | null;
  metadataId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AppRecord {
  id: string;
  name: string;
  color: string;
  text: string;
  wordmark: string;
  letter: string;
  logoUrl: string | null;
  tag: string;
  androidSlug: string | null;
  tizenId: string | null;
  webosId: string | null;
  iosScheme: string | null;
  webUrl: string | null;
  /** Certification programme (`AppCertification`) or null — see
   *  `@shared/app-certification`. Platform-admin field. */
  certification: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LicenseSeatSummary {
  totalSeats: number;
  unlimited: boolean;
  activatedCount: number;
  demoCount: number;
  hasFreeSeats: boolean;
}

export type LicenseStatus = "active" | "pending" | "cancelled";

/**
 * Which screen population a licence row covers — see the server's
 * `ScreenCategory` (`License.ts`) for the full story. Same three literal
 * values on purpose: this is meant to become the connector between a
 * licence and the rooms/screens that draw against it.
 */
export type ScreenCategory = "room" | "public" | "common_area";

/** Display labels for {@link ScreenCategory} — the vocabulary Sigge chose. */
export const SCREEN_CATEGORY_LABELS: Record<ScreenCategory, string> = {
  room: "Hotel/Care",
  common_area: "Common area",
  public: "Public viewing",
};

/** Render order for the three categories, wherever they're listed together. */
export const SCREEN_CATEGORIES: readonly ScreenCategory[] = [
  "room",
  "common_area",
  "public",
];

/**
 * One offering a premise holds, with what it bought and what it handed out.
 *
 * `bought` is SUMMED across licences, and `null` means unbounded — a site
 * licence stays a site licence however many seats sit beside it.
 */
export interface OfferingCompliance {
  featureSet: string;
  name: string;
  bought: number | null;
  seatUnit: string | null;
  assigned: number;
  /** Positive means more rooms hold it than were bought. Never acted on. */
  over: number;
}

export interface LicenseMoney {
  currency?: string;
  amount?: number;
  fractionDigits?: number;
}

/**
 * One additional purchase LINE on a licence, on top of its own base
 * `quantity`/`price` — see the server's `LicenseDocument.tiers`. Exists
 * because a premise growing its purchase at a different price ("100 rooms
 * at a held 2024 rate, 20 more at today's") is real, and a second `License`
 * row for the same product is refused since the 2026-09-07 ruling.
 */
export interface LicenseTierRecord {
  id: string;
  quantity: number | null;
  price: LicenseMoney | null;
  notes: string | null;
  addedAt: string;
}

export interface LicenseRecord {
  id: string;
  premiseId: string;
  /** ⚠️ Legacy free-text capability name. Superseded by `featureSet`. */
  product: string;
  /** `FeatureSet._id` — what they bought. */
  featureSet: string | null;
  /** Agreed price, when it differs from the product's rate card. */
  price: LicenseMoney | null;
  status: LicenseStatus;
  /** Which screen population this row covers. `null` = not yet classified. */
  screenCategory: ScreenCategory | null;
  quantity: number | null;
  /** Additional purchase lines beyond the base `quantity`/`price` above. */
  tiers: LicenseTierRecord[];
  /** `quantity` plus every tier's - the number that actually matters. */
  totalQuantity: number | null;
  notes: string | null;
  /** Who withdrew it, and when. Null unless the licence is cancelled. */
  cancelledAt: string | null;
  cancelledBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LicenseInput {
  premiseId: string;
  product?: string;
  featureSet?: string | null;
  price?: LicenseMoney | null;
  status?: LicenseStatus;
  screenCategory?: ScreenCategory | null;
  quantity?: number | null;
  notes?: string | null;
}

export interface AssetSummary {
  url: string;
  filename: string;
  originalName: string;
  size: number;
  contentType: string;
  /** Virtual folder path ("" = root). */
  folder: string;
  /** Owning premise slug, or `null` when not premise-owned. */
  premiseId: string | null;
  /** Owning company id, or `null` when not company-owned. */
  companyId: string | null;
  /** Which layer owns this file. `"company"` inside a location's Content view
   *  means inherited: usable, but not editable there. */
  scope: "global" | "company" | "premise";
  /** Pixel dimensions, when the uploader measured them. Null on older files. */
  width: number | null;
  height: number | null;
  /** Clip length in seconds; null for stills and unmeasured files. */
  durationSeconds: number | null;
  /**
   * How this clip's normalisation for panels is getting on.
   *
   * The rendition REPLACES the upload in place, so there is no second file and
   * no second row: `state` is the whole story, and `url` is always null.
   *
   * 🚨 `panel: null` means no transcode was ever asked for — every image, and
   * every clip uploaded before this existed. Those are perfectly usable and
   * play exactly as they always have, so `null` must never render as "broken",
   * only as "nothing to say".
   */
  panel?: {
    state: "queued" | "running" | "ready" | "failed";
    url: string | null;
    error: string | null;
  } | null;
  /** A still from a clip, as a data URI. Null for images and older clips. */
  posterUrl: string | null;
  /** Derived shape - landscape 16:9, portrait 9:16, or custom. */
  orientation: MediaOrientation | null;
  uploadedAt: string;
  /**
   * ISO date this file is destroyed automatically, or null (BE-29).
   *
   * Nullable, and every consumer must keep it that way: most files have no
   * purge date. A date in the PAST on a file that is still listed means the
   * sweep tried and was refused because something still displays it - see
   * `purgeStateOf` in `@shared/media`.
   */
  purgeAt: string | null;
}

/** What kind of thing is holding a media file. Mirrors the server's union. */
export type AssetHolderKind =
  | "signage-playlist"
  | "signage-campaign"
  | "scheme-content"
  | "care-photo"
  | "app-catalogue"
  | "radio-station"
  | "call-contact"
  | "content-owner"
  | "room-offer"
  | "diagnostics";

/** One thing that would break if the file were deleted. */
export interface AssetUsage {
  kind: AssetHolderKind;
  id: string;
  label: string;
  premiseId: string | null;
}

/**
 * The answer to "what is using this file?".
 *
 * `elsewhere` is a COUNT, not a truncation: holders belonging to another
 * customer are deliberately never named on a tenant-scoped route. See the
 * server's `assetUsageReply.ts`.
 */
export interface AssetUsageResponse {
  usedBy: AssetUsage[];
  elsewhere: number;
}

/** What to call each holder kind in a sentence. Matches the CMS's own English. */
const ASSET_HOLDER_LABEL: Record<AssetHolderKind, string> = {
  "signage-playlist": "playlist",
  "signage-campaign": "campaign",
  "scheme-content": "template content at",
  "care-photo": "care photo",
  "app-catalogue": "app",
  "radio-station": "radio station",
  "call-contact": "call contact",
  // `content-owner` was on the server union and missing here, so a 409 naming
  // it fell through to the raw kind. The record is exhaustive over
  // `AssetHolderKind` on purpose: adding a holder server-side and not here is
  // how the refusal reaches an operator as an unlabelled row.
  "content-owner": "content owner",
  "room-offer": "offer",
  diagnostics: "diagnostics artefact",
};

/**
 * Turn a 409 from an asset delete into one sentence.
 *
 * Lives beside the types rather than in a component because all three media
 * surfaces (global library, a location's Content, a chain's Content) hit the
 * same refusal and must not each invent their own wording. Returns null for
 * any other error, so a caller can fall through to its normal handling.
 */
export function assetInUseMessage(err: unknown): string | null {
  const e = err as { code?: string; body?: AssetUsageResponse } | null;
  if (!e || e.code !== "asset_in_use") return null;
  const usedBy = e.body?.usedBy ?? [];
  const elsewhere = e.body?.elsewhere ?? 0;
  const named = usedBy
    .slice(0, 4)
    .map((u) => `${ASSET_HOLDER_LABEL[u.kind] ?? u.kind} "${u.label}"`);
  const rest = usedBy.length - named.length;
  if (rest > 0) named.push(`${rest} more`);
  if (elsewhere > 0) {
    named.push(
      `${elsewhere} other location${elsewhere === 1 ? "" : "s"} ` +
        "(an administrator can see which)"
    );
  }
  return `Still in use, so it can't be deleted: ${named.join(", ")}.`;
}
